import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { MapContainer, TileLayer, LayersControl, GeoJSON, Marker, Popup, Pane, useMapEvents, useMap } from "react-leaflet";
import GeoTIFFLayer from "./GeoTIFFLayer";
import MapLegend from "../components/maplegend";
import "leaflet/dist/leaflet.css";
import L from "leaflet";
import Papa from "papaparse";
import { rgb } from "geotiff";
import GroupedVerticalLegends from "../components/rangebarlegend";
import { LocalizationProvider } from '@mui/x-date-pickers';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import TextField from '@mui/material/TextField';
import { useLocation } from 'react-router-dom';


import {
  DndContext, 
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

import { useTheme, useMediaQuery, IconButton, Drawer, Box } from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import CloseIcon from '@mui/icons-material/Close';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import Tooltip from '@mui/material/Tooltip';
import { Line, Bar } from 'react-chartjs-2';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    PointElement,
    LineElement,
    Title,
    Tooltip as ChartTooltip,
    Legend
} from 'chart.js';

import RidgePredictor from './/ridge_predictor'; 
import ComparisonSlider from '../components/ComparisonSlider';

const API_BASE_URL = process.env.REACT_APP_API_URL

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, ChartTooltip, Legend);

const legendCSVUrl = process.env.PUBLIC_URL + "/legend.csv";

// Helper component to fetch GeoJSON data from a URL
const GeoJsonWithUrl = ({ url, ...props }) => {
    const [data, setData] = useState(null);
  
    useEffect(() => {
      let isMounted = true;
      fetch(url)
        .then(response => {
          if (!response.ok) {
            throw new Error(`Failed to fetch GeoJSON from ${url}`);
          }
          return response.json();
        })
        .then(geojsonData => {
          if (isMounted) {
            setData(geojsonData);
          }
        })
        .catch(error => {
          console.error("Error fetching or parsing GeoJSON:", error);
          if (isMounted) {
            setData(null);
          }
        });
      
      return () => {
        isMounted = false;
      };
    }, [url]);
  
    return data ? <GeoJSON data={data} {...props} /> : null;
};

const { BaseLayer } = LayersControl;

// Enhanced SidebarNavItem component with mobile awareness
const SidebarNavItem = ({ category, label, icon, bgColor, activeCategory, setActiveCategory, textColor = 'inherit', isMobile }) => (
    <div 
        className={`sidebar-item ${activeCategory === category ? 'active' : ''}`}
        onClick={() => setActiveCategory(category)}
        style={{
            padding: isMobile ? '0.8vh 0' : '1vh 0',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            cursor: 'pointer',
            backgroundColor: activeCategory === category ? '#d5d3c3' : 'transparent',
            borderLeft: activeCategory === category ? '4px solid #4a7c59' : '4px solid transparent',
        }}
    >
        <div style={{ 
            width: isMobile ? '32px' : 'min(40px, 4vh)', 
            height: isMobile ? '32px' : 'min(40px, 4vh)',
            backgroundColor: bgColor, 
            borderRadius: '5px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
        }}>
            <span role="img" aria-label={label} style={{ color: textColor, fontSize: isMobile ? '14px' : 'min(16px, 1.8vh)' }}>{icon}</span>
        </div>
        <div style={{ fontSize: isMobile ? '9px' : 'min(12px, 1.4vh)', marginTop: isMobile ? '3px' : '0.5vh', textAlign: 'center' }}>
            {label.split(' ').map((word, i) => (
                <React.Fragment key={i}>
                    {word}
                    {i < label.split(' ').length - 1 && <br />}
                </React.Fragment>
            ))}
        </div>
    </div>
);

const MapController = ({ comparisonMode, district, mangroveToFileMap, croplandToFileMap, layerType }) => {
    const map = useMap();

    useEffect(() => {
        if (comparisonMode && district) {
            // Disable map dragging and zooming
            map.dragging.disable();
            map.touchZoom.disable();
            map.doubleClickZoom.disable();
            map.scrollWheelZoom.disable();
            map.boxZoom.disable();
            map.keyboard.disable();

            // Try to zoom to the district (you'll need to add district bounds data)
            // For now, we'll just prevent interaction
            console.log('Map interactions disabled for comparison mode');
        } else {
            // Re-enable map interactions
            map.dragging.enable();
            map.touchZoom.enable();
            map.doubleClickZoom.enable();
            map.scrollWheelZoom.enable();
            map.boxZoom.enable();
            map.keyboard.enable();

            console.log('Map interactions enabled');
        }
    }, [comparisonMode, district, map]);

    return null;
};

const Monitoring = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const isTablet = useMediaQuery(theme.breakpoints.down('md'));
    
    // Mobile drawer states
    const [sidebarDrawerOpen, setSidebarDrawerOpen] = useState(false);
    const [contentDrawerOpen, setContentDrawerOpen] = useState(false);
    
    // Drawer toggle functions
    const toggleSidebarDrawer = () => setSidebarDrawerOpen(!sidebarDrawerOpen);
    const toggleContentDrawer = () => setContentDrawerOpen(!contentDrawerOpen);
    

    const sensors = useSensors(
        useSensor(PointerSensor),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        })
    );

    // Navigation items config array
    const navigationItems = [
        { category: 'active', label: 'Active Layers', icon: '📋', bgColor: '#4a7c59', textColor: '#fff' },
        { category: 'mangrove', label: 'Mangrove Coverage', icon: '🌴', bgColor: '#97bf8f' },
        { category: 'forest', label: 'Forest Coverage', icon: '🌲', bgColor: '#b8d8b2' },
        { category: 'fire', label: 'Fire Alerts and Disturbance', icon: '🔥', bgColor: '#333333', textColor: '#ff6b35' },
        { category: 'weather', label: 'Weather Data', icon: '⛅', bgColor: '#a7c5eb' },
        { category: 'cropland', label: 'Cropland Extent', icon: '🌾', bgColor: '#f0e68c' },
        { category: 'peatland', label: 'Peatland Distribution', icon: '🌱', bgColor: '#8b4513', textColor: '#fff' },
        { category: 'risk', label: 'Risk Index', icon: '⚠️', bgColor: '#e74c3c', textColor: '#fff' }
    ];

    //const [selectedYear, setSelectedYear] = useState("2020");
    const [selectedMangroveYear, setSelectedMangroveYear] = useState("2020"); 
    const [selectedForestYear, setSelectedForestYear] = useState("2020");   
    const [selectedCroplandYear, setSelectedCroplandYear] = useState("2019"); 

    const [isComparingMangroves, setIsComparingMangroves] = useState(false);
    const [mangroveCompareYears, setMangroveCompareYears] = useState({ a: '2010', b: '2020' });

    const [visibleLayers, setVisibleLayers] = useState({
        cropland: false
    });
    const [divisionData, setDivisionData] = useState({
        Kuching: ['Kuching', 'Bau', 'Lundu'],
        Samarahan: ['Samarahan', 'Asajaya', 'Simunjan'],
        Serian: ['Serian'],
        Sri_Aman: ['Sri Aman', 'Lubok Antu'],
        Betong: ['Betong', 'Saratok'],
        Sarikei: ['Sarikei', 'Pakan', 'Julau', 'Meradong'],
        Sibu: ['Sibu', 'Kanowit', 'Selangau'],
        Mukah: ['Mukah', 'Dalat', 'Matu', 'Daro'],
        Bintulu: ['Bintulu', 'Tatau'],
        Kapit: ['Kapit', 'Belaga', 'Song'],
        Miri: ['Miri', 'Marudi'],
        Limbang: ['Limbang', 'Lawas']
    });

    const districtToFileMap = {
        'Asajaya': 'forest1',
        'Bau': 'forest2',
        'Belaga': 'forest3',
        'Betong': 'forest4',
        'Bintulu': 'forest5',
        'Dalat': 'forest6',
        'Daro': 'forest7',
        'Julau': 'forest8',
        'Kanowit': 'forest9',
        'Kapit': 'forest10',
        'Kuching': 'forest11',
        'Lawas': 'forest12',
        'Limbang': 'forest13',
        'Lubok Antu': 'forest14',
        'Lundu': 'forest15',
        'Marudi': 'forest16',
        'Matu': 'forest17',
        'Meradong': 'forest18',
        'Miri': 'forest19',
        'Mukah': 'forest20',
        'Pakan': 'forest21',
        'Samarahan': 'forest22',
        'Saratok': 'forest23',
        'Sarikei': 'forest24',
        'Selangau': 'forest25',
        'Serian': 'forest26',
        'Sibu': 'forest27',
        'Simunjan': 'forest28',
        'Song': 'forest29',
        'Sri_Aman': 'forest30',
        'Tatau': 'forest31'
    };

    const mangroveToFileMap = {
        'Asajaya': 'mangrove1',
        'Betong': 'mangrove2',
        'Dalat': 'mangrove3',
        'Daro': 'mangrove4',
        'Kuching': 'mangrove5',
        'Lawas': 'mangrove6',
        'Limbang': 'mangrove7',
        'Lundu': 'mangrove8',
        'Matu': 'mangrove9',
        'Meradong': 'mangrove10',
        'Miri': 'mangrove11',
        'Mukah': 'mangrove12',
        'Samarahan': 'mangrove13',
        'Saratok': 'mangrove14',
        'Sarikei': 'mangrove15',
        'Simunjan': 'mangrove16',
        'Sri Aman': 'mangrove17'
    }

    const croplandToFileMap = {
        'Asajaya': 'cropland1',
        'Bau': 'cropland2',
        'Belaga': 'cropland3',
        'Betong': 'cropland4',
        'Bintulu': 'cropland5',
        'Dalat': 'cropland6',
        'Daro': 'cropland7',
        'Julau': 'cropland8',
        'Kanowit': 'cropland9',
        'Kapit': 'cropland10',
        'Kuching': 'cropland11',
        'Lawas': 'cropland12',
        'Limbang': 'cropland13',
        'Lubok Antu': 'cropland14',
        'Lundu': 'cropland15',
        'Marudi': 'cropland16',
        'Matu': 'cropland17',
        'Meradong': 'cropland18',
        'Miri': 'cropland19',
        'Mukah': 'cropland20',
        'Pakan': 'cropland21',
        'Samarahan': 'cropland22',
        'Saratok': 'cropland23',
        'Sarikei': 'cropland24',
        'Selangau': 'cropland25',
        'Serian': 'cropland26',
        'Sibu': 'cropland27',
        'Simunjan': 'cropland28',
        'Song': 'cropland29',
        'Sri Aman': 'cropland30',
        'Tatau': 'cropland31'
    };

    const cropland2019ToFileMap = {
        'Asajaya': 'cropland2019_1',
        'Bau': 'cropland2019_2',
        'Belaga': 'cropland2019_3',
        'Betong': 'cropland2019_4',
        'Bintulu': 'cropland2019_5',
        'Dalat': 'cropland2019_6',
        'Daro': 'cropland2019_7',
        'Julau': 'cropland2019_8',
        'Kanowit': 'cropland2019_9',
        'Kapit': 'cropland2019_10',
        'Kuching': 'cropland2019_11',
        'Lawas': 'cropland2019_12',
        'Limbang': 'cropland2019_13',
        'Lubok Antu': 'cropland2019_14',
        'Lundu': 'cropland2019_15',
        'Marudi': 'cropland2019_16',
        'Matu': 'cropland2019_17',
        'Meradong': 'cropland2019_18',
        'Miri': 'cropland2019_19',
        'Mukah': 'cropland2019_20',
        'Pakan': 'cropland2019_21',
        'Samarahan': 'cropland2019_22',
        'Saratok': 'cropland2019_23',
        'Sarikei': 'cropland2019_24',
        'Selangau': 'cropland2019_25',
        'Serian': 'cropland2019_26',
        'Sibu': 'cropland2019_27',
        'Simunjan': 'cropland2019_28',
        'Song': 'cropland2019_29',
        'Sri Aman': 'cropland2019_30',
        'Tatau': 'cropland2019_31'
    };

    const cropland2019AvailableDistricts = new Set([
        // Districts that have /vector/cropland/2019/oilpalm_*.geojson
        'Asajaya',     
        'Belaga',
        'Betong',
        'Bintulu',
        'Dalat',
        'Daro',    
        'Kanowit',      
        'Lawas',
        'Limbang',    
        'Marudi',
        'Matu',      
        'Miri',
        'Mukah',  
        'Samarahan',
        'Saratok',    
        'Selangau',
        'Serian',
        'Sibu',
        'Simunjan',
        'Sri Aman',
        'Tatau'
    ]);

    const peatlandAvailableDistricts = new Set([
        'Asajaya',
        'Betong',
        'Bintulu',
        'Dalat',
        'Daro',
        'Julau',
        'Kanowit',
        'Kuching',
        'Lawas',
        'Limbang',
        'Lundu',
        'Marudi',
        'Matu',
        'Meradong',
        'Miri',
        'Mukah',
        'Samarahan',
        'Saratok',
        'Sarikei',
        'Selangau',
        'Serian',
        'Sibu',
        'Simunjan',
        'Sri Aman',
        'Tatau'
    ]);

    const peatlandToFileMap = {
        'Asajaya': 'peatlandAsajaya',
        'Bau': 'peatlandBau',
        'Belaga': 'peatlandBelaga',
        'Betong': 'peatlandBetong',
        'Bintulu': 'peatlandBintulu',
        'Dalat': 'peatlandDalat',
        'Daro': 'peatlandDaro',
        'Julau': 'peatlandJulau',
        'Kanowit': 'peatlandKanowit',
        'Kapit': 'peatlandKapit',
        'Kuching': 'peatlandKuching',
        'Lawas': 'peatlandLawas',
        'Limbang': 'peatlandLimbang',
        'Lubok Antu': 'peatlandLubokAntu',
        'Lundu': 'peatlandLundu',
        'Marudi': 'peatlandMarudi',
        'Matu': 'peatlandMatu',
        'Meradong': 'peatlandMeradong',
        'Miri': 'peatlandMiri',
        'Mukah': 'peatlandMukah',
        'Pakan': 'peatlandPakan',
        'Samarahan': 'peatlandSamarahan',
        'Saratok': 'peatlandSaratok',
        'Sarikei': 'peatlandSarikei',
        'Selangau': 'peatlandSelangau',
        'Serian': 'peatlandSerian',
        'Sibu': 'peatlandSibu',
        'Simunjan': 'peatlandSimunjan',
        'Song': 'peatlandSong',
        'Sri Aman': 'peatlandSriAman',
        'Tatau': 'peatlandTatau'
    };

    const [layerOrder, setLayerOrder] = useState([]);

    const [activeCategory, setActiveCategory] = useState("mangrove");

    const [mangroveLayersVisible, setMangroveLayersVisible] = useState({});
    const [forestLayersVisible, setForestLayersVisible] = useState({});
    const [croplandLayersVisible, setCroplandLayersVisible] = useState({});
    const [cropland2019LayersVisible, setCropland2019LayersVisible] = useState({});
    const [mangroveInfoOpen, setMangroveInfoOpen] = useState(false);
    const [forestInfoOpen, setForestInfoOpen] = useState(false);
    const [croplandInfoOpen, setCroplandInfoOpen] = useState(false);
    const [peatlandInfoOpen, setPeatlandInfoOpen] = useState(false); // Add this
    const [peatlandLayersVisible, setPeatlandLayersVisible] = useState({});

    const [hideEmptyMangroveDistricts, setHideEmptyMangroveDistricts] = useState(false);
    const [hideEmptyCroplandDistricts, setHideEmptyCroplandDistricts] = useState(false);
    const [hideEmptyPeatlandDistricts, setHideEmptyPeatlandDistricts] = useState(false); // Add this

    const [infoPopup, setInfoPopup] = useState(null); // State for the info popup

    const [mangroveStatsByYear, setMangroveStatsByYear] = useState({});

    const [legendsVisible, setLegendsVisible] = useState(!isMobile);
    const [expandedDivisions, setExpandedDivisions] = useState({});

    // Comparison mode state
    const [comparisonMode, setComparisonMode] = useState(false);
    const [comparisonConfig, setComparisonConfig] = useState({
        layerType: null, // 'mangrove' or 'cropland'
        district: '',
        leftYear: '',
        rightYear: '',
        sliderPosition: 50
    });
    const [comparisonBounds, setComparisonBounds] = useState(null);

    // Initialize comparison config based on the active category
    useEffect(() => {
        // Only initialize for categories that support comparison
        if (activeCategory === 'mangrove' || activeCategory === 'forest' || activeCategory === 'cropland') {
            handleStartComparison(activeCategory);
        }
    }, [activeCategory]); // Run when activeCategory changes

    const comparisonStyleRef = useRef(null);

    // useEffect(() => {
    //     if (!comparisonMode) {
    //         // Remove comparison styles when not in comparison mode
    //         const existingStyle = document.getElementById('comparison-clip-styles');
    //         if (existingStyle) {
    //             existingStyle.remove();
    //         }
    //         return;
    //     }

    //     const { sliderPosition } = comparisonConfig;
        
    //     // Create or update dynamic style element
    //     let styleEl = document.getElementById('comparison-clip-styles');
    //     if (!styleEl) {
    //         styleEl = document.createElement('style');
    //         styleEl.id = 'comparison-clip-styles';
    //         document.head.appendChild(styleEl);
    //     }

    //     // Apply clip-path to the actual Leaflet panes (not the React components)
    //     styleEl.textContent = `
    //         .leaflet-pane.leaflet-comparison-left-pane-pane {
    //             clip-path: inset(0 ${100 - sliderPosition}% 0 0) !important;
    //             z-index: 650 !important;
    //         }
    //         .leaflet-pane.leaflet-comparison-right-pane-pane {
    //             clip-path: inset(0 0 0 ${sliderPosition}%) !important;
    //             z-index: 650 !important;
    //         }
    //     `;

    //     console.log(`Updated clip-path styles with slider at ${sliderPosition}%`);

    //     // Cleanup function
    //     return () => {
    //         const el = document.getElementById('comparison-clip-styles');
    //         if (el) {
    //             el.remove();
    //         }
    //     };
    // }, [comparisonMode, comparisonConfig.sliderPosition]);

    // Risk prediction state variables 
    const [riskPrediction, setRiskPrediction] = useState(null);
    const [predictionLoading, setPredictionLoading] = useState(false);
    const [predictionError, setPredictionError] = useState(null);
    const [predictor] = useState(new RidgePredictor());
    const [riskInputs, setRiskInputs] = useState({
        slope: '',
        elevation: '',
        precipitation: '',
        temperature: '',
        cropSuitability: '',
        distanceUrban: '',
        distanceAirport: '',
        irrigationArea: '',
        soilDrainage: '',
        humanInfluence: '',
        population: '',
        biomassStock: '',
        ecoregion: 'borneo'
    });

    const [riskTab, setRiskTab] = useState('fire'); // 'forest' or 'fire'
    const [fireInputs, setFireInputs] = useState({
        timeStep: 12 // default to 12 months
    });
    const [firePrediction, setFirePrediction] = useState(null);
    const [firePredictionLoading, setFirePredictionLoading] = useState(false);
    const [firePredictionError, setFirePredictionError] = useState(null);

    const [selectedFireModel, setSelectedFireModel] = useState('XGBoost');

    // Clear prediction results when the model is changed
    useEffect(() => {
        setFirePrediction(null);
        setFirePredictionError(null);
    }, [selectedFireModel]);

    useEffect(() => {
        const loadPredictor = async () => {
            await predictor.loadModel();
        };
        loadPredictor();
    }, [predictor]);

    // Normalize / helpers
    const normalizeDistrictForPath = (name) => {
        if (!name) return '';
        return name.replace(/\s+/g, '_');
    }

    // Add prediction function
    const handleRiskPrediction = async () => {
        if (!predictor.isLoaded) {
            setPredictionError('Model is not loaded yet. Please try again.');
            return;
        }

        setPredictionLoading(true);
        setPredictionError(null);
        
        try {
            // Validate inputs
            const requiredFields = ['slope', 'elevation', 'precipitation', 'temperature', 'cropSuitability', 
                                'distanceUrban', 'distanceAirport', 'irrigationArea', 'soilDrainage', 
                                'humanInfluence', 'population', 'biomassStock'];
            
            for (const field of requiredFields) {
                if (!riskInputs[field] || riskInputs[field] === '') {
                    throw new Error(`Please fill in all fields. Missing: ${field}`);
                }
            }

            // Prepare input data for prediction
            const inputData = {
                'Slope': parseFloat(riskInputs.slope),
                'Elevation': parseFloat(riskInputs.elevation),
                'Precipitation': parseFloat(riskInputs.precipitation),
                'Temp_mean_year_1970-2000': parseFloat(riskInputs.temperature),
                'Crop Suitability Index (Mean)': parseFloat(riskInputs.cropSuitability),
                'Distance to Closest Urban (metres)': parseFloat(riskInputs.distanceUrban),
                'Distance to Closest Airports': parseFloat(riskInputs.distanceAirport),
                'Irrigation Area (Hectar)': parseFloat(riskInputs.irrigationArea),
                'Soil Drainage (Mean)': parseFloat(riskInputs.soilDrainage),
                'Human influence index': parseFloat(riskInputs.humanInfluence),
                'Population 2000': parseFloat(riskInputs.population),
                'whrc_aboveground_biomass_stock_2000__Mg': parseFloat(riskInputs.biomassStock),
                'Ecoregion_Borneo Lowland and Montane Forests': riskInputs.ecoregion === 'borneo' ? 1 : 0,
                'Ecoregion_Greater Sundas Mangroves': riskInputs.ecoregion === 'mangroves' ? 1 : 0
            };

            // Make prediction
            const prediction = predictor.predict(inputData);
            setRiskPrediction(prediction);
            
        } catch (error) {
            setPredictionError(error.message);
        } finally {
            setPredictionLoading(false);
        }
    };

    const handleFirePrediction = async () => {
        setFirePredictionLoading(true);
        setFirePredictionError(null);
        setFirePrediction(null); // Clear previous prediction
        
        try {
            if (!fireInputs.timeStep || fireInputs.timeStep < 1 || fireInputs.timeStep > 60) {
                throw new Error('Please enter a valid time step (1-60 months)');
            }

            // Use a configurable API base URL (more deployment-friendly)
            const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';
            
            let endpoint;
            if (selectedFireModel === 'prophet') {
                endpoint = `${API_BASE_URL}/predict-fire-prophet`;
            } else if (selectedFireModel === 'lstm') {
                endpoint = `${API_BASE_URL}/predict-fire-lstm`;
            } else {
                endpoint = `${API_BASE_URL}/predict-fire`; // Default to XGBoost
            }

            const response = await fetch(endpoint, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    timeStep: parseInt(fireInputs.timeStep)
                })
            });

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({ detail: 'Failed to get fire prediction' }));
                throw new Error(errorData.detail || 'Failed to get fire prediction');
            }

            const result = await response.json();
            setFirePrediction(result);
            
        } catch (error) {
            setFirePredictionError(error.message);
        } finally {
            setFirePredictionLoading(false);
        }
    };

    const toggleExpandedDivisions = (division) => {
        setExpandedDivisions((prev) => ({
            ...prev,
            [division]: !prev[division]
        }));
    };

    const toggleMangroveLayer = (district) => {
        setMangroveLayersVisible((prev) => ({
            ...prev,
            [district]: !prev[district]
        }));
    };

    const toggleForestLayer = (district) => {
        setForestLayersVisible((prev) => ({
            ...prev,
            [district]: !prev[district]
        }));
    };

    const toggleCroplandLayer = (district) => {
        setCroplandLayersVisible((prev) => ({
            ...prev,
            [district]: !prev[district]
        }));
    };

    const toggleCropland2019Layer = (district) => {
        setCropland2019LayersVisible(prev => ({ 
            ...prev, 
            [district]: !prev[district] 
        }));
    };

    const togglePeatlandLayer = (district) => {
        setPeatlandLayersVisible((prev) => ({
            ...prev,
            [district]: !prev[district]
        }));
    };

    const toggleLayer = (key) => {
        setVisibleLayers((prev) => ({
            ...prev,
            [key]: !prev[key]
        }));
    };

    // Comparison mode handlers
    const handleStartComparison = (layerType) => {
        const config = { ...comparisonConfig };
        config.layerType = layerType;
        
        // Set default years based on layer type
        if (layerType === 'mangrove') {
            config.leftYear = '2010';
            config.rightYear = '2020';
            config.district = Object.keys(mangroveToFileMap)[0]; // Default to first district
        } else if (layerType === 'forest') {
            config.leftYear = '2010';
            config.rightYear = '2020';
            config.district = Object.keys(districtToFileMap)[0];
        } else if (layerType === 'cropland') {
            config.leftYear = '2015';
            config.rightYear = '2019';
            config.district = Object.keys(croplandToFileMap)[0];
        }
        
        setComparisonConfig(config);
    };

    const getDistrictBounds = (district, layerType) => {
        // Pre-defined bounding boxes for each district
        // Format: [south, west, north, east]
        const districtBounds = {
            'Asajaya':     [1.32, 110.42, 1.58, 110.68],
            'Bau':         [1.36, 109.95, 1.67, 110.33],
            'Belaga':      [2.35, 113.50, 3.30, 114.80],
            'Betong':      [1.00, 111.36, 1.33, 111.74],
            'Bintulu':     [2.95, 112.95, 3.55, 113.65],
            'Dalat':       [2.65, 112.30, 3.00, 112.70],
            'Daro':        [2.55, 111.75, 2.95, 112.20],
            'Julau':       [1.90, 111.74, 2.35, 112.15],
            'Kanowit':     [2.05, 112.10, 2.55, 112.65],
            'Kapit':       [1.75, 112.30, 2.60, 114.00],
            'Kuching':     [1.42, 110.18, 1.73, 110.56],
            'Lawas':       [4.75, 115.25, 5.15, 115.62],
            'Limbang':     [4.55, 114.85, 5.03, 115.35],
            'Lubok_Antu':  [0.95, 111.70, 1.45, 112.25],
            'Lundu':       [1.57, 109.78, 1.92, 110.12],
            'Marudi':      [3.35, 114.00, 4.10, 115.00],
            'Matu':        [2.45, 111.80, 2.75, 112.10],
            'Meradong':    [1.90, 111.50, 2.20, 111.90],
            'Miri':        [4.18, 113.80, 4.65, 114.28],
            'Mukah':       [2.75, 112.05, 3.30, 112.60],
            'Pakan':       [1.95, 111.97, 2.30, 112.29],
            'Samarahan':   [1.29, 110.28, 1.63, 110.63],
            'Saratok':     [1.55, 111.20, 1.92, 111.55],
            'Sarikei':     [1.75, 111.35, 2.07, 111.62],
            'Selangau':    [2.50, 112.10, 3.10, 112.70],
            'Serian':      [1.05, 110.35, 1.45, 110.85],
            'Sibu':        [2.16, 111.75, 2.60, 112.20],
            'Simunjan':    [1.18, 110.49, 1.43, 110.78],
            'Song':        [2.05, 112.35, 2.55, 113.20],
            'Sri_Aman':    [0.98, 111.20, 1.47, 111.70],
            'Tatau':       [2.60, 112.35, 3.10, 113.10],
        };
        
        return districtBounds[district] || [0.5, 109.5, 5.5, 115.5]; // Default to all Sarawak
    };

    const handleExitComparison = () => {
        setComparisonMode(false);
        setComparisonBounds(null);
        setComparisonConfig({
            layerType: null,
            district: '',
            leftYear: '',
            rightYear: '',
            sliderPosition: 50
        });

        // Re-initialize comparison config after exiting
        // Use setTimeout to ensure state is cleared first
        setTimeout(() => {
            if (activeCategory === 'mangrove' || activeCategory === 'forest' || activeCategory === 'cropland') {
                handleStartComparison(activeCategory);
            }
        }, 0);
    };

    // Callback to receive bounds from GeoTIFFLayer
    const handleBoundsLoad = useCallback((bounds, district, year) => {
        if (comparisonMode && district === comparisonConfig.district) {
            setComparisonBounds(prevBounds => {
                // If we already have bounds, extend them
                // Otherwise, just use the new bounds
                if (!prevBounds) {
                    console.log(`First bounds received for ${district} (${year}):`, bounds.toBBoxString());
                    return bounds;
                }
                
                // Extend to include both years' data
                const extended = prevBounds.extend(bounds);
                console.log(`Extended bounds for ${district} to include ${year}:`, extended.toBBoxString());
                return extended;
            });
        }
    }, [comparisonMode, comparisonConfig.district]);

    // Update ZoomToDistrict to use actual GeoTIFF bounds
    const ZoomToDistrict = ({ district, layerType, shouldZoom }) => {
        const map = useMap();
        const [hasZoomed, setHasZoomed] = useState(false);
        
        useEffect(() => {
            if (!shouldZoom || !district) {
                setHasZoomed(false);
                return;
            }
            
            // Wait for comparisonBounds to be loaded from GeoTIFF
            if (!comparisonBounds) {
                console.log('Waiting for GeoTIFF bounds to load...');
                return;
            }
            
            // Only zoom once
            if (hasZoomed) {
                console.log('Already zoomed, skipping...');
                return;
            }
            
            // Small delay to ensure everything is ready
            const timer = setTimeout(() => {
                try {
                    // Validate bounds before zooming
                    const sw = comparisonBounds.getSouthWest();
                    const ne = comparisonBounds.getNorthEast();
                    
                    // Check if bounds are reasonable (within Sarawak)
                    const isValidBounds = 
                        sw.lat >= 0.5 && sw.lat <= 5.5 &&
                        sw.lng >= 109.5 && sw.lng <= 115.5 &&
                        ne.lat >= 0.5 && ne.lat <= 5.5 &&
                        ne.lng >= 109.5 && ne.lng <= 115.5;
                    
                    if (!isValidBounds) {
                        console.warn('Invalid bounds detected, using fallback:', comparisonBounds.toBBoxString());
                        // Use fallback hardcoded bounds for this district
                        const fallbackBounds = getDistrictBounds(district, layerType);
                        const leafletBounds = L.latLngBounds(
                            [fallbackBounds[0], fallbackBounds[1]],
                            [fallbackBounds[2], fallbackBounds[3]]
                        );
                        map.fitBounds(leafletBounds, {
                            padding: [80, 80],
                            maxZoom: 13,
                            animate: true,
                            duration: 0.5
                        });
                    } else {
                        // Use actual GeoTIFF bounds
                        map.fitBounds(comparisonBounds, {
                            padding: [80, 80],
                            maxZoom: 13,
                            animate: true,
                            duration: 0.5
                        });
                        
                        console.log(`Zoomed to ${district} using GeoTIFF bounds:`, comparisonBounds.toBBoxString());
                    }
                    
                    setHasZoomed(true);
                } catch (error) {
                    console.error('Error zooming to bounds:', error);
                }
            }, 500); // Increased delay to 500ms
            
            return () => clearTimeout(timer);
        }, [shouldZoom, district, layerType, map, comparisonBounds, hasZoomed]);
        
        return null;
    };

    const handleActivateComparison = async () => {
        if (!comparisonConfig.district) {
            alert('Please select a district to compare');
            return;
        }

        // Reset bounds when starting new comparison
        setComparisonBounds(null);  
        setComparisonMode(true);
        
        // Clear other active layers to avoid confusion
        clearAllMangroveLayers();
        clearAllForestLayers();
        clearAllCroplandLayers();
        clearAllPeatlandLayers();
        setSelectedDate("");
        setWeatherMarkers([]);

        // The ZoomToDistrict component will handle the actual zooming
    };

    const [downloadYear, setDownloadYear] = useState("all");
    const [allMangroveVisible, setAllMangroveVisible] = useState(false);
    const [allForestVisible, setAllForestVisible] = useState(false);
    const [allCroplandVisible, setAllCroplandVisible] = useState(false);
    const [allCropland2019Visible, setAllCropland2019Visible] = useState(false);
    const [allPeatlandVisible, setAllPeatlandVisible] = useState(false);
    const [weatherMarkers, setWeatherMarkers] = useState([]);
    const [weatherDate, setWeatherDate] = useState("");

    const [weatherLocation, setWeatherLocation] = useState("Kuching");
    const [weatherLoading, setWeatherLoading] = useState(false);
    const [weatherError, setWeatherError] = useState(null);
    const [weatherApiSource, setWeatherApiSource] = useState("openweather"); 
    const [weatherRangeStart, setWeatherRangeStart] = useState("");
    const [weatherRangeEnd, setWeatherRangeEnd] = useState("");
    const [weatherRangeLocation, setWeatherRangeLocation] = useState("Kuching");
    const [weatherMonthlyAverages, setWeatherMonthlyAverages] = useState([]);
    const [weatherRangeLoading, setWeatherRangeLoading] = useState(false);
    const [weatherRangeError, setWeatherRangeError] = useState(null);

    const handleToggleAllMangroveLayers = () => {
        const newVisibility = !allMangroveVisible;
        setAllMangroveVisible(newVisibility);

        Object.keys(mangroveToFileMap).forEach(district => {
            if (!!mangroveLayersVisible[district] !== newVisibility) {
                toggleMangroveLayer(district);
                toggleLayer(mangroveToFileMap[district]);
            }
        });
    };
    
    const handleToggleAllForestLayers = async () => {
        const newVisibility = !allForestVisible;
        setAllForestVisible(newVisibility);

        const districts = Object.keys(districtToFileMap);

        for (let i = 0; i < districts.length; i++) {
            const district = districts[i];
            const isCurrentlyVisible = !!forestLayersVisible[district];

            if (isCurrentlyVisible !== newVisibility) {
            toggleForestLayer(district);
            toggleLayer(districtToFileMap[district]);

            // Add a short delay to prevent rendering issues
            await new Promise(resolve => setTimeout(resolve, 1000));
            }
        }
    };

    const handleToggleAllCroplandLayers = () => {
        const newVisibility = !allCroplandVisible;
        setAllCroplandVisible(newVisibility);

        Object.keys(croplandToFileMap).forEach(district => {
            if (!!croplandLayersVisible[district] !== newVisibility) {
                toggleCroplandLayer(district);
                toggleLayer(croplandToFileMap[district]);
            }
        });
    };

    const handleToggleAllCropland2019Layers = () => {
        const newVisibility = !allCropland2019Visible;
        setAllCropland2019Visible(newVisibility);

        Object.keys(cropland2019ToFileMap).forEach(district => {
            if (!!cropland2019LayersVisible[district] !== newVisibility) {
                toggleCropland2019Layer(district);
                toggleLayer(cropland2019ToFileMap[district]);
            }
        });
    };

    const handleToggleAllPeatlandLayers = () => {
        const newVisibility = !allPeatlandVisible;
        setAllPeatlandVisible(newVisibility);

        // Only toggle districts that actually have peatland data
        peatlandAvailableDistricts.forEach((district) => {
            if (!!peatlandLayersVisible[district] !== newVisibility) {
                togglePeatlandLayer(district);
                const key = peatlandToFileMap[district];
                if (key) toggleLayer(key); // FIX: actually toggle the layer key
            }
        });
    };
    // Handler for clicks on GeoTIFF layers
    const handleLayerClick = async (latlng, value, layerType, district) => {
        let content = "No data";
        
        // Use the layerType to determine the content
        if (layerType === 'mangrove') {
            if (value !== 1) { setInfoPopup({ position: latlng, content: <span>No mangrove at this pixel</span> }); return; }

            const year = selectedMangroveYear;
            const stats = await getMangroveStats(year, district);

            if (stats) {
                const cov = stats.coverage != null ? Number(stats.coverage).toFixed(2) : 'N/A';
                const area = stats.area != null ? Number(stats.area).toLocaleString(undefined, { maximumFractionDigits: 2 }) : 'N/A';

                content = (
                    <div>
                        <strong>{district}</strong>
                        <br />
                        Mangrove coverage: {cov}%
                        <br />
                        Mangrove area: {area}km²
                        <br />
                        Year: {year}
                    </div>
                );
            } else {
                content = (
                    <div>
                        <strong>{district}</strong>
                        <br />
                        No stats found for {year}
                    </div>
                );
            }
        } else if (layerType === 'forest') {
            // Ensure lookup tables are ready
            if (!forestClassLookup) {
                content = "Land Cover data is still loading. Please try again.";
            } else {
                // Fetch the specific district's histogram data
                const districtHistogram = await getForestHistogramStats(selectedForestYear, district);

                if (!districtHistogram) {
                    content = `No land cover breakdown available for ${district} in ${selectedForestYear}.`;
                } else {
                    const histoCounts = {};
                    let totalPixels = 0;

                    // Aggregate pixel counts by SpecificClass
                    for (const key in districtHistogram) {
                        if (key.startsWith('HISTO_')) {
                            const mapValue = key.split('_')[1];
                            const specificClass = forestClassLookup.get(mapValue) || 'Other';
                            const count = districtHistogram[key];

                            if (count > 0) {
                                histoCounts[specificClass] = (histoCounts[specificClass] || 0) + count;
                                totalPixels += count;
                            }
                        }
                    }

                    // Determine the land type for the specific clicked pixel
                    let clickedLandType = 'No Data';
                    if (value !== 0) {
                        const landInfo = forestColorMap.get(value);
                        if (landInfo) {
                            const parts = [landInfo.specificClass, landInfo.subClass].filter(Boolean);
                            if (parts.length > 0) {
                                clickedLandType = parts.join(' - ');
                            } else {
                                clickedLandType = landInfo.generalClass || 'N/A';
                            }
                        } else {
                            clickedLandType = `Unknown (Value: ${value})`;
                        }
                    }

                    // Build the popup content as a React component 
                    const sortedClasses = Object.entries(histoCounts).sort(([, a], [, b]) => b - a);
                    content = (
                        <div style={{ fontFamily: 'sans-serif', fontSize: '14px', maxHeight: '200px', overflowY: 'auto', paddingRight: '10px' }}>
                            <strong style={{ fontSize: '16px' }}>{district}</strong><br/>
                            <em style={{ color: '#555' }}>Land Cover ({selectedForestYear})</em>
                            <p style={{ margin: '5px 0' }}>Clicked Land Type: <strong>{clickedLandType}</strong></p>
                            <hr style={{ margin: '4px 0' }}/>
                            {totalPixels > 0 ? (
                                <ul style={{ listStyleType: 'disc', paddingLeft: '20px', margin: '5px 0' }}>
                                    {sortedClasses.map(([className, count]) => {
                                        const percentage = ((count / totalPixels) * 100).toFixed(2);
                                        return (
                                            <li key={className} style={{ marginBottom: '3px' }}>
                                                {className}: <strong>{percentage}%</strong>
                                            </li>
                                        );
                                    })}
                                </ul>
                            ): (
                                <p>No land cover data available.</p>
                            )}
                        </div>
                    );
                }
            }
        } else if (layerType === 'cropland') {
            if (value === 1) {
                content = "Cropland Present";
            }
        } else {
            // Fallback for any other layer type
            if (value !== 0 && value !== undefined) {
                content = `Pixel Value: ${value}`;
            }
        }

        console.log(`Setting popup at ${latlng} with content: "${content}"`); 

        setInfoPopup({
            position: latlng,
            content: content
        });
    };

    const extractMangroveStats = (props = {}) => {
        const coverage = props.mang_pct ?? null;
        const area = props.mang_area ?? null;
        return { coverage, area };
    };

    // Fetch and cache stats for a district/year
    const getMangroveStats = async (year, district) => {
        if (!year || !district) return null;

        // Return from cache if present
        const cached = mangroveStatsByYear?.[year]?.[district];
        if (cached) return cached;

        const districtPaths = [
            `/vector/mangrove/${year}/${district}_stats.geojson`,
            `/vector/mangrove/${year}/${normalizeDistrictForPath(district)}_stats.geojson`
        ];

        let stats = null;

        for (const path of districtPaths) {
            try {
                const res = await fetch(path);
                if (!res.ok) continue;
                const gj = await res.json();
                const feat = (gj.features && gj.features[0]) || null;
                if (feat?.properties) {
                    const extracted = extractMangroveStats(feat.properties);
                    stats = { ...extracted, props: feat.properties };
                    break;
                }
            } catch (e) {
                // Ignore and try next path
            }
        }

        setMangroveStatsByYear(prev => ({
            ...prev,
            [year]: {
                ...(prev[year] || {}),
                [district]: stats
            }
        }));

        return stats;
    }

    const mangroveRegistryRef = useRef(new Map()); 

    const registerMangroveLayer = useCallback((district, getValueAt) => {
        mangroveRegistryRef.current.set(district, getValueAt);
    }, []);

    const unregisterMangroveLayer = useCallback((district) => {
        mangroveRegistryRef.current.delete(district);
    }, []);

    const MangroveClickHandler = () => {
        useMapEvents({
            click: async (e) => {
                if (mangroveRegistryRef.current.size === 0) return;

                // Find fist district whose pixel == 1 at the click
                let hit = null;
                for (const [district, getValueAt] of mangroveRegistryRef.current.entries()) {
                    try {
                        const val = getValueAt(e.latlng);
                        if (val === 1) {
                            hit = { district, val };
                            break;
                        }
                    } catch {}
                }

                if (hit) {
                    await handleLayerClick(e.latlng, 1, 'mangrove', hit.district);
                } else {
                    setInfoPopup({ position: e.latlng, content: <span>No mangrove at this pixel</span> });
                }
            }
        });
        
        return null;
    }

    // These functions clear layers directly 

    const clearAllMangroveLayers = () => {
        // Only clear districts that are currently visible
        Object.keys(mangroveLayersVisible).forEach(district => {
            if (mangroveLayersVisible[district]) {
                toggleMangroveLayer(district);
                toggleLayer(mangroveToFileMap[district]);
            }
        });
        setAllMangroveVisible(false);
    };

    const clearAllForestLayers = () => {
        // Only clear districts that are currently visible
        Object.keys(forestLayersVisible).forEach(district => {
            if (forestLayersVisible[district]) {
                toggleForestLayer(district);
                toggleLayer(districtToFileMap[district]);
            }
        });
        setAllForestVisible(false);
    };

    const clearAllCroplandLayers = () => {
        if (selectedCroplandYear === "2019") {
            // Clear 2019 districts
            Object.keys(cropland2019LayersVisible).forEach(district => {
                if (cropland2019LayersVisible[district]) {
                    toggleCropland2019Layer(district);
                    toggleLayer(cropland2019ToFileMap[district]);
                }
            });
            setAllCropland2019Visible(false);
        } else {
            // Clear 2015 districts
            Object.keys(croplandLayersVisible).forEach(district => {
                if (croplandLayersVisible[district]) {
                    toggleCroplandLayer(district);
                    toggleLayer(croplandToFileMap[district]);
                }
            });
            setAllCroplandVisible(false);
        }
    };

    const clearAllPeatlandLayers = () => {
        Object.keys(peatlandLayersVisible).forEach(district => {
            if (peatlandLayersVisible[district]) {
                togglePeatlandLayer(district);
                toggleLayer(peatlandToFileMap[district]);
            }
        });
        setAllPeatlandVisible(false);
    };

    const handleDragEnd = (event) => {
        const { active, over } = event;
        
        if (active.id !== over.id) {
            setLayerOrder((items) => {
            const oldIndex = items.findIndex(item => item.id === active.id);
            const newIndex = items.findIndex(item => item.id === over.id);
            
            return arrayMove(items, oldIndex, newIndex);
            });
        }
    };

    // State for slider date index and animation
    const [sliderDateIndex, setSliderDateIndex] = useState(0);
    const [isPlaying, setIsPlaying] = useState(false);
    const [animationInterval, setAnimationInterval] = useState(null);
    const [fireData, setFireData] = useState([]);
    const [uniqueDates, setUniqueDates] = useState([]);
    const [selectedDate, setSelectedDate] = useState("");
    const [filteredData, setFilteredData] = useState([]);
    const [selectedAlert, setSelectedAlert] = useState(null);
    const [fireLoading, setFireLoading] = useState(false);
    const totalAlerts = filteredData.length;
    const confidenceCounts = { l: 0, n: 0, h: 0 };
    const [trendsDialogOpen, setTrendsDialogOpen] = useState(false);
    const fireTrends = useMemo(() => {
        return uniqueDates.map(date => ({
            date,
            count: fireData.filter(alert => alert.acq_date === date).length
        }));
    }, [fireData, uniqueDates]);

    // helper to compute available years from uniqueDates (reuse existing uniqueDates state)
    const availableDownloadYears = useMemo(() => {
        return [
            "all",
            ...new Set(uniqueDates.map(d => d && d.slice(0,4)).filter(Boolean))
        ];
    }, [uniqueDates]);

    // download handler (place inside component)
const handleDownloadByYear = async () => {
    try {
        // Build base (use working combined endpoint)
        const envBase = (typeof API_BASE_URL !== 'undefined' && API_BASE_URL) ? API_BASE_URL : (process.env.REACT_APP_API_URL || '');
        const devFallback = (process.env.NODE_ENV === 'development') ? 'http://localhost:8000' : '';
        const base = (envBase || devFallback || window.location.origin).replace(/\/+$/, '');

        // Use the existing working endpoint that returns the full archive
        const combinedUrl = `${base}/api/fires/combined`;
        console.log("Fetching combined CSV from:", combinedUrl);

        const res = await fetch(combinedUrl);
        if (!res.ok) throw new Error(`Failed to fetch combined archive: ${res.status} ${res.statusText}`);
        const csvText = await res.text();

        // Parse CSV (Papa is already used in this file)
        const parsed = Papa.parse(csvText, { header: true, skipEmptyLines: true });
        const rows = parsed.data || [];

        // Filter by selected year (downloadYear === "all" returns everything)
        const filtered = (downloadYear && downloadYear !== "all")
            ? rows.filter(r => (r.acq_date || "").toString().startsWith(downloadYear))
            : rows;

        if (!filtered.length) {
            alert("No records for selected year.");
            return;
        }

        // Build CSV text from filtered rows (preserve header order)
        const headers = parsed.meta.fields || Object.keys(filtered[0]);
        const csvRows = [
            headers.join(","),
            ...filtered.map(row => headers.map(h => {
                const v = row[h];
                // Escape double-quotes
                if (v === null || v === undefined) return "";
                const s = String(v).replace(/"/g, '""');
                return s.includes(",") || s.includes('"') || s.includes("\n") ? `"${s}"` : s;
            }).join(","))
        ].join("\r\n");

        const blob = new Blob([csvRows], { type: "text/csv" });
        const filename = downloadYear === "all" ? 'sarawak_fires_full.csv' : `sarawak_fires_${downloadYear}.csv`;
        const link = document.createElement('a');
        link.href = window.URL.createObjectURL(blob);
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(link.href);

    } catch (err) {
        console.error("Download error:", err);
        alert("Failed to download CSV. See console for details.");
    }
};


    const fireTrendsChartData = {
        labels: fireTrends.map(item => item.date),
        datasets: [
            {
                label: 'Fire Alerts',
                data: fireTrends.map(item => item.count),
                fill: false,
                borderColor: 'red',
                backgroundColor: 'rgba(255,99,132,0.2)',
                tension: 0.2,
            }
        ]
    };
    filteredData.forEach(alert => {
        if (alert.confidence) {
            confidenceCounts[alert.confidence] = (confidenceCounts[alert.confidence] || 0) + 1;
        }
    });

    const availableTrendYears = useMemo(() => {
        return [
            ...new Set(fireData
                .map(row => row.date && row.date.slice(0, 4)) // <-- Changed from row.acq_date
                .filter(Boolean)
            )
        ].sort();
    }, [fireData]);

    const [trendYear, setTrendYear] = useState("all");

    // 2. Filter fireTrends for the selected year
    const fireTrendsFiltered = useMemo(() => {
        // fireData is now [{date: "YYYY-MM-DD", count: 123}, ...]
        if (!fireData || fireData.length === 0) {
            return [];
        }

        let filtered = fireData;
        
        if (trendYear !== "all") {
            // Filter by the 'date' property, which is a string
            filtered = fireData.filter(row => row.date && row.date.startsWith(trendYear));
        }

        // The data is already in the {date, count} format we need.
        // Just return the filtered list.
        return filtered;
        
    }, [fireData, trendYear]);

    const fireTrendsChartDataFiltered = {
        labels: fireTrendsFiltered.map(item => item.date),
        datasets: [
            {
                label: 'Fire Alerts',
                data: fireTrendsFiltered.map(item => item.count),
                fill: false,
                borderColor: 'red',
                backgroundColor: 'rgba(255,99,132,0.2)',
                tension: 0.2,
            }
        ]
    };

    useEffect(() => {
        if (isPlaying && uniqueDates.length > 0) {
            const interval = setInterval(() => {
                setSliderDateIndex(prev => {
                    const next = prev + 1;
                    if (next >= uniqueDates.length) {
                        setIsPlaying(false);
                        return prev;
                    }
                    setSelectedDate(uniqueDates[next]);
                    return next;
                });
            }, 1000); // 1 second per frame
            setAnimationInterval(interval);
            return () => clearInterval(interval);
        } else if (animationInterval) {
            clearInterval(animationInterval);
        }
        // eslint-disable-next-line
    }, [isPlaying, uniqueDates]);

    useEffect(() => {
        if (selectedDate) {
            const idx = uniqueDates.indexOf(selectedDate);
            if (idx !== -1) setSliderDateIndex(idx);
        }
    }, [selectedDate, uniqueDates]);

    // useEffect for layer order 
    useEffect(() => {
        // Instead of individual districts, group by layer type
        const layerGroups = {
            mangrove: {
                visible: false,
                districts: [],
                year: selectedMangroveYear 
            },
            forest: {
                visible: false,
                districts: [],
                year: selectedForestYear   
            },
            cropland: {
                visible: false,
                districts: [],
                year: selectedCroplandYear 
            },
            peatland: {
                visible: false,
                districts: [],
                year: '2019'
            }
        };
        
        // Group districts by layer type
        Object.keys(mangroveLayersVisible).forEach(district => {
            if (mangroveLayersVisible[district]) {
                layerGroups.mangrove.visible = true;
                layerGroups.mangrove.districts.push(district);
            }
        });
        
        Object.keys(forestLayersVisible).forEach(district => {
            if (forestLayersVisible[district]) {
                layerGroups.forest.visible = true;
                layerGroups.forest.districts.push(district);
            }
        });
        
        Object.keys(croplandLayersVisible).forEach(district => {
        if (croplandLayersVisible[district] && selectedCroplandYear === "2015") {
                layerGroups.cropland.visible = true;
                layerGroups.cropland.districts.push(district);
            }
        });
        
        Object.keys(cropland2019LayersVisible).forEach(district => {
            if (cropland2019LayersVisible[district] && selectedCroplandYear === "2019") {
                layerGroups.cropland.visible = true;
                layerGroups.cropland.districts.push(district);
            }
        });

        Object.keys(peatlandLayersVisible).forEach(district => {
            if (peatlandLayersVisible[district]) {
                layerGroups.peatland.visible = true;
                layerGroups.peatland.districts.push(district);
            }
        });
        
        // active layer IDs array with grouped items
        const activeLayerIds = [];
        
        if (layerGroups.mangrove.visible) {
            activeLayerIds.push({
                id: `mangrove-${selectedMangroveYear}`,
                type: 'mangrove',
                districts: layerGroups.mangrove.districts,
                year: selectedMangroveYear
            });
        }
        
        if (layerGroups.forest.visible) {
            activeLayerIds.push({
                id: `forest-${selectedForestYear}`,
                type: 'forest',
                districts: layerGroups.forest.districts,
                year: selectedForestYear
            });
        }
        
        if (layerGroups.cropland.visible) {
            activeLayerIds.push({
                id: `cropland-${selectedCroplandYear}`,
                type: 'cropland',
                districts: layerGroups.cropland.districts,
                year: selectedCroplandYear
            });
        }
        
        if (layerGroups.peatland.visible) {
            activeLayerIds.push({
                id: 'peatland-2019',
                type: 'peatland',
                districts: layerGroups.peatland.districts,
                year: '2019'
            });
        }
        
        if (selectedDate && filteredData.length > 0) {
            activeLayerIds.push({
                id: `fire-${selectedDate}`,
                type: 'fire',
                date: selectedDate
            });
        }

        if (weatherMarkers && weatherMarkers.length > 0) {
            weatherMarkers.forEach(marker => {
                activeLayerIds.push({
                    id: `weather-${marker.location}-${marker.date}`,
                    type: 'weather',
                    coords: { lat: marker.lat, lon: marker.lon },
                    location: marker.location,
                    date: marker.date,
                    data: marker.data
                });
            });
        }
        
        console.log("Active layer groups:", activeLayerIds);

        // Update layer order, preserving existing order for layers that remain active
        setLayerOrder(prevOrder => {
            const newOrder = [];
            
            // keep previously ordered items that are still active
            prevOrder.forEach(item => {
                // Match by type and year instead of by district
                const matchingLayer = activeLayerIds.find(layer => 
                    layer.type === item.type && 
                    (layer.type === 'peatland' ? layer.year === item.year : 
                    layer.type === 'fire' ? layer.date === item.date : 
                    layer.type === 'weather' ? (layer.date === item.date && layer.location === item.location) :
                    layer.year === item.year)
                );
                
                if (matchingLayer) {
                    // Update districts list if needed
                    newOrder.push(matchingLayer);
                }
            });
            
            // add new items that weren't in the previous order
            activeLayerIds.forEach(item => {
                const exists = newOrder.some(layer => 
                    layer.type === item.type && 
                    (layer.type === 'peatland' ? layer.year === item.year : 
                    layer.type === 'fire' ? layer.date === item.date : 
                    layer.type === 'weather' ? (layer.date === item.date && layer.location === item.location) :
                    layer.year === item.year)
                );
                
                if (!exists) {
                    newOrder.push(item);
                }
            });

            console.log("New layer order:", newOrder);
            return newOrder;
        });
    }, [mangroveLayersVisible, forestLayersVisible, croplandLayersVisible, cropland2019LayersVisible, peatlandLayersVisible, selectedDate, filteredData, selectedMangroveYear, selectedForestYear, selectedCroplandYear, weatherMarkers, weatherDate, weatherLocation]); // Update dependencies


    useEffect(() => {
        // Only fetch if the fire tab is active AND dates haven't been loaded
        if (activeCategory === 'fire' && uniqueDates.length === 0) { 
            
            const fetchFireDates = async () => {
                try {
                    setFireLoading(true);

                    // Fetch 1: The unique dates for the slider
                    // Fetch from the new 'available-dates' endpoint
                    const res = await fetch(`${API_BASE_URL}/fires/available-dates`); 
                    if (!res.ok) throw new Error("Failed to fetch fire dates");

                    const dates = await res.json(); // Get the JSON array of dates
                    
                    // console.log("Unique dates:", dates);
                    setUniqueDates(dates); // Set the slider dates

                    // Fetch 2: The pre-calculated daily counts for the chart
                    const countsRes = await fetch(`${API_BASE_URL}/fires/daily-counts`);
                    if (!countsRes.ok) throw new Error("Failed to fetch daily counts");
                    const counts = await countsRes.json();
                    console.log("Daily counts:", counts);
                    setFireData(counts);

                } catch (error) {
                    console.error("Error fetching fire history data:", error);
                } finally {
                    setFireLoading(false);
                }
            };
            
            fetchFireDates();
        }
    }, [activeCategory, uniqueDates.length, API_BASE_URL]);

    useEffect(() => {
        // Fetches data whenever the selectedDate changes
        if (selectedDate) {
            const fetchFiresForDate = async () => {
                setFireLoading(true); // Show loading spinner
                try {
                    // Fetch ONLY the data for the selected date
                    const res = await fetch(`${API_BASE_URL}/fires/by-date?date=${selectedDate}`);
                    if (!res.ok) {
                        throw new Error(`Failed to fetch fire data for ${selectedDate}`);
                    }
                    const csvText = await res.text();
                    
                    Papa.parse(csvText, {
                        header: true,
                        skipEmptyLines: true,
                        complete: (result) => {
                            // Set the filteredData directly.
                            setFilteredData(result.data || []); 
                        },
                    });

                } catch (error) {
                    console.error("Error fetching fire alerts:", error);
                    setFilteredData([]); // Clear data on error
                } finally {
                    setFireLoading(false); // Hide loading spinner
                }
            };

            fetchFiresForDate();

        } else {
            // If no date is selected, clear the markers
            setFilteredData([]);
        }
    }, [selectedDate, API_BASE_URL]); // Dependency: only selectedDate

    const getMarkerColor = (confidence) => {
        switch (confidence) {
            case "l":
                return "green";
            case "n":
                return "orange";
            case "h":
                return "red";
            default:
                return "blue";
        }
    };

    const [weatherTempDialogOpen, setWeatherTempDialogOpen] = useState(false);
    const [weatherPrecipDialogOpen, setWeatherPrecipDialogOpen] = useState(false);
    const [weatherHumidityDialogOpen, setWeatherHumidityDialogOpen] = useState(false);
    const [weatherWindDialogOpen, setWeatherWindDialogOpen] = useState(false);
    
    const weatherIcon = new L.Icon({
      iconUrl: "https://cdn.jsdelivr.net/npm/leaflet@1.7.1/dist/images/marker-icon.png",
      iconSize: [25, 41],
      iconAnchor: [12, 41],
      popupAnchor: [1, -34],
      shadowUrl: "https://cdn.jsdelivr.net/npm/leaflet@1.7.1/dist/images/marker-shadow.png",
      shadowSize: [41, 41],
    });

    function getWeatherDescription(code) {
        const weatherCodeMap = {
            0: "Clear sky",
            1: "Mainly clear",
            2: "Partly cloudy",
            3: "Overcast",
            45: "Fog",
            48: "Depositing rime fog",
            51: "Light drizzle",
            53: "Moderate drizzle",
            55: "Dense drizzle",
            56: "Light freezing drizzle",
            57: "Dense freezing drizzle",
            61: "Slight rain",
            63: "Moderate rain",
            65: "Heavy rain",
            66: "Light freezing rain",
            67: "Heavy freezing rain",
            71: "Slight snow fall",
            73: "Moderate snow fall",
            75: "Heavy snow fall",
            77: "Snow grains",
            80: "Slight rain showers",
            81: "Moderate rain showers",
            82: "Violent rain showers",
            85: "Slight snow showers",
            86: "Heavy snow showers",
            95: "Thunderstorm",
            96: "Thunderstorm with slight hail",
            99: "Thunderstorm with heavy hail"
        };
        return weatherCodeMap[code] || `Unknown (${code})`;
    }

    async function getCoordinates(city, apiKey) {
        const cityQuery = city.trim().toLowerCase();
        const url = `https://api.openweathermap.org/geo/1.0/direct?q=${encodeURIComponent(cityQuery)},MY&limit=1&appid=${apiKey}`;
        const res = await fetch(url);
        const data = await res.json();
        console.log("Geocoding result:", data);
        if (Array.isArray(data) && data.length > 0) {
            return { lat: data[0].lat, lon: data[0].lon };
        }
        throw new Error("Location not found");
    }

    // OpenWeatherMap fetch
    async function fetchOpenWeather(lat, lon, date, apiKey) {
        const start = Math.floor(new Date(date + "T00:00:00Z").getTime() / 1000);
        const end = Math.floor(new Date(date + "T23:59:59Z").getTime() / 1000);
        const url = `https://history.openweathermap.org/data/2.5/history/city?lat=${lat}&lon=${lon}&type=hour&start=${start}&end=${end}&appid=${apiKey}&units=metric`;
        const res = await fetch(url);
        if (!res.ok) throw new Error("Failed to fetch weather");
        const data = await res.json();
        return data;
    }

    // Open-Meteo fetch
    async function fetchOpenMeteo(lat, lon, startDate, endDate) {
        const url = `https://archive-api.open-meteo.com/v1/archive?latitude=${lat}&longitude=${lon}&start_date=${startDate}&end_date=${endDate}&hourly=temperature_2m,relative_humidity_2m,windspeed_10m,precipitation,weathercode`;
        const res = await fetch(url);
        if (!res.ok) throw new Error("Failed to fetch weather");
        const data = await res.json();
        if (!data.hourly || !data.hourly.time) return { list: [] };
        const list = data.hourly.time.map((time, idx) => ({
            dt: new Date(time).getTime() / 1000,
            main: {
                temp: data.hourly.temperature_2m[idx],
                humidity: data.hourly.relative_humidity_2m[idx]
            },
            weather: [{ description: getWeatherDescription(data.hourly.weathercode[idx]) }],
            wind: { speed: data.hourly.windspeed_10m[idx] },
            precipitation: data.hourly.precipitation ? data.hourly.precipitation[idx] : 0
        }));
        return { list };
    }
    
    async function handleWeatherMonthlyAverages() {
        setWeatherRangeLoading(true);
        setWeatherRangeError(null);
        setWeatherMonthlyAverages([]);

        try {
            const apiKey = process.env.REACT_APP_WEATHER_API_KEY;
            const { lat, lon } = await getCoordinates(weatherRangeLocation, apiKey);

            // Parse start and end months
            const start = new Date(weatherRangeStart + "-01");
            const end = new Date(weatherRangeEnd + "-01");
            const results = [];

            let current = new Date(start);
            while (current <= end) {
                const year = current.getFullYear();
                const month = (current.getMonth() + 1).toString().padStart(2, "0");
                const firstDay = `${year}-${month}-01`;
                // Get last day of month
                const lastDay = new Date(year, current.getMonth() + 1, 0).getDate();
                const lastDate = `${year}-${month}-${lastDay}`;

                let data;
                if (weatherApiSource === "openweather") {
                    // Fetch each day in the month and aggregate
                    let allItems = [];
                    for (let d = 1; d <= lastDay; d++) {
                        const dateStr = `${year}-${month}-${d.toString().padStart(2, "0")}`;
                        const dayData = await fetchOpenWeather(lat, lon, dateStr, apiKey);
                        if (dayData && dayData.list && dayData.list.length > 0) {
                            allItems = allItems.concat(dayData.list);
                        }
                    }
                    data = { list: allItems };
                } else {
                    // Open-Meteo supports date ranges
                    data = await fetchOpenMeteo(lat, lon, firstDay, lastDate);
                }

                // Calculate averages
                let tempSum = 0, humiditySum = 0, windSum = 0, precipSum = 0;
                let count = 0;
                if (data && data.list && data.list.length > 0) {
                    data.list.forEach(item => {
                        tempSum += item.main.temp;
                        humiditySum += item.main.humidity;
                        windSum += item.wind.speed;
                        if (item.rain && (item.rain['1h'] || item.rain['3h'])) {
                            precipSum += item.rain['1h'] || item.rain['3h'] || 0;
                        } else if (item.precipitation) {
                            precipSum += item.precipitation;
                        }
                    });
                    count = data.list.length;
                }
                results.push({
                    month: `${year}-${month}`,
                    temp: count ? (tempSum / count).toFixed(2) : "-",
                    precip: count ? (precipSum / count).toFixed(2) : "-",
                    humidity: count ? (humiditySum / count).toFixed(0) : "-",
                    wind: count ? (windSum / count).toFixed(2) : "-"
                });

                // Move to next month
                current.setMonth(current.getMonth() + 1);
            }

            setWeatherMonthlyAverages(results);
        } catch (err) {
            setWeatherRangeError(err.message);
        } finally {
            setWeatherRangeLoading(false);
        }
    }

    useEffect(() => {
        async function fetchWeather() {
            if (activeCategory === "weather" && weatherLocation && weatherDate) {
                setWeatherMarkers([]);
                setWeatherLoading(true);
                setWeatherError(null);

                try {
                    const apiKey = process.env.REACT_APP_WEATHER_API_KEY;
                    const { lat, lon } = await getCoordinates(weatherLocation, apiKey);

                    let data;
                    if (weatherApiSource === "openweather") {
                        data = await fetchOpenWeather(lat, lon, weatherDate, apiKey);
                    } else {
                        data = await fetchOpenMeteo(lat, lon, weatherDate, weatherDate);
                    }

                    // Add new marker to the array
                    setWeatherMarkers(prev => [
                        ...prev,
                        {
                            lat,
                            lon,
                            location: weatherLocation,
                            date: weatherDate,
                            data
                        }
                    ]);
                } catch (err) {
                    setWeatherError(err.message);
                } finally {
                    setWeatherLoading(false);
                }
            }
        }
        fetchWeather();
    }, [activeCategory, weatherLocation, weatherDate, weatherApiSource]);

    function getWeatherAverages(weatherData) {
        if (!weatherData || !weatherData.list || weatherData.list.length === 0) return null;
        const count = weatherData.list.length;
        let tempSum = 0, humiditySum = 0, windSum = 0, precipSum = 0;
        const weatherTypes = {};
        weatherData.list.forEach(item => {
            tempSum += item.main.temp;
            humiditySum += item.main.humidity;
            windSum += item.wind.speed;
            // OpenWeatherMap uses item.rain?.['1h'] or item.rain?.['3h'] for precipitation
            if (item.rain && (item.rain['1h'] || item.rain['3h'])) {
                precipSum += item.rain['1h'] || item.rain['3h'] || 0;
            } else if (item.precipitation) {
                precipSum += item.precipitation;
            }
            const desc = item.weather[0].description;
            weatherTypes[desc] = (weatherTypes[desc] || 0) + 1;
        });
        const mostCommonWeather = Object.entries(weatherTypes).sort((a, b) => b[1] - a[1])[0]?.[0] || "";
        return {
            weather: mostCommonWeather,
            temp: (tempSum / count).toFixed(1),
            precip: (precipSum / count).toFixed(4),
            humidity: (humiditySum / count).toFixed(0),
            wind: (windSum / count).toFixed(1)
        };
    }

    function downloadWeatherAveragesCSV() {
        if (!weatherMonthlyAverages.length) return;
        const header = "month,temp (°C),precip (mm),humidity (%),wind (m/s)";
        const rows = weatherMonthlyAverages.map(row =>
            [row.month, row.temp, row.precip, row.humidity, row.wind].join(",")
        );
        const csvContent = [header, ...rows].join("\r\n");
        const blob = new Blob([csvContent], { type: "text/csv" });
        const url = URL.createObjectURL(blob);

        const a = document.createElement("a");
        a.href = url;
        a.download = `weather_monthly_averages.csv`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    }

    const weatherMonthlyTempData = {
    labels: weatherMonthlyAverages.map(row => row.month),
    datasets: [{
        label: 'Avg Temp (°C)',
        data: weatherMonthlyAverages.map(row => parseFloat(row.temp)),
        borderColor: '#1976d2',
        backgroundColor: 'rgba(25, 118, 210, 0.2)',
        fill: true,
        }]
    };

    const weatherMonthlyPrecipData = {
        labels: weatherMonthlyAverages.map(row => row.month),
        datasets: [{
            label: 'Avg Precip (mm)',
            data: weatherMonthlyAverages.map(row => parseFloat(row.precip)),
            borderColor: '#e74c3c',
            backgroundColor: 'rgba(231, 76, 60, 0.2)',
            fill: true,
        }]
    };

    const weatherMonthlyHumidityData = {
        labels: weatherMonthlyAverages.map(row => row.month),
        datasets: [{
            label: 'Avg Humidity (%)',
            data: weatherMonthlyAverages.map(row => parseFloat(row.humidity)),
            borderColor: '#00b894',
            backgroundColor: 'rgba(0, 184, 148, 0.2)',
            fill: true,
        }]
    };

    const weatherMonthlyWindData = {
        labels: weatherMonthlyAverages.map(row => row.month),
        datasets: [{
            label: 'Avg Wind (m/s)',
            data: weatherMonthlyAverages.map(row => parseFloat(row.wind)),
            borderColor: '#636e72',
            backgroundColor: 'rgba(99, 110, 114, 0.2)',
            fill: true,
        }]
    };

    const [forestLegendItems, setForestLegendItems] = useState([]);
    const [forestColorMap, setForestColorMap] = useState(new Map());
    const [forestHistogramData, setForestHistogramData] = useState({});
    const [forestClassLookup, setForestClassLookup] = useState(null);

    useEffect(() => {
    fetch(legendCSVUrl)
        .then(res => res.text())
        .then(csvText => {
            Papa.parse(csvText, {
                header: true,
                skipEmptyLines: true,
                complete: (result) => {
                    console.log(result.data); 
                    const items = result.data
                        .map(row => ({
                            general: row["General class"],
                            class: row["Class"],
                            color: "#" + row["Color code"],
                            label: row["Sub-class"]
                        }));

                    const grouped = {};
                    items.forEach(item => {
                        if (!grouped[item.general]) grouped[item.general] = {};
                        if (!grouped[item.general][item.class]) grouped[item.general][item.class] = [];
                        grouped[item.general][item.class].push({color: item.color, label: item.label});
                    });
                    setForestLegendItems(grouped);
                }
            });
        });
    }, []);

    useEffect(() => {
        const loadForestColors = async () => {
            try {
                const res = await fetch('/forest_colors.json');
                const data = await res.json();
                const colorMap = new Map();
                const classLookup = new Map();
                data.forEach(item => {
                    colorMap.set(item.MapValue, {subClass: item.SubClass, specificClass: item.SpecificClass, generalClass: item.GeneralClass});
                    classLookup.set(item.MapValue.toString(), item.SpecificClass);
                });
                setForestColorMap(colorMap);
                setForestClassLookup(classLookup);
            } catch (err) {
                console.error("Failed to load forest_colors.json", err);
            }
        };
        loadForestColors();
    }, []);

    // Helper function to fetch and cache histogram data for a specific district
    const getForestHistogramStats = async (year, district) => {
        // Check cache first
        const cachedData = forestHistogramData[year]?.[district];
        if (cachedData) {
            return cachedData;
        }

        // If not cached, fetch the data
        const districtPath = normalizeDistrictForPath(district);
        const url = `/tif/forest/${year}/stats/histo_${districtPath}.geojson`;

        try {
            const res = await fetch(url);
            if (!res.ok) {
                console.error(`Failed to fetch histogram for ${district} at ${url}`);
                return null;
            }
            const geojsonData = await res.json();
            const properties = geojsonData.features?.[0]?.properties;

            if (properties) {
                // Cache the fetched data
                setForestHistogramData(prevData => ({
                    ...prevData,
                    [year]: {
                        ...prevData[year],
                        [district]: properties,
                    },
                }));
                return properties;
            }
            return null;
        } catch (error) {
            console.error(`Error fetching histogram for ${district}:`, error);
            return null;
        }
    };

    // CSS styles of Active Layers 
    const activeLayerStyles = `
        .active-layer {
            background-color: #f5f5f5;
            padding: 12px 15px;
            margin-bottom: 15px;
            border-radius: 5px;
            border: 3px solid #4a7c59;
            position: relative;
            padding-left: 24px;
            transition: all 0.2s ease;
            box-shadow: 0 1px 3px rgba(0,0,0,0.1);
        }
        .active-layer:hover {
            background-color: #f0f0f0;
            box-shadow: 0 10px 20px rgba(0,0,0,0.15);
        }
        .active-layer h4 {
            margin-top: 0;
            margin-bottom: 8px;
            display: flex;
            align-items: center;
            font-size: 0.95em;
        }
        .active-layer p {
            margin: 5px 0 10px 0;
            font-size: 0.9em;
            color: #555;
        }
        .active-layers-list {
            padding-top: 5px;
            padding-bottom: 5px;
        }
        
        /* Mobile styles */
        @media (max-width: 600px) {
            .leaflet-control-zoom {
                margin-bottom: 60px !important;
            }
        }
    `;

    // Sortable Item for Active Layers instead of just Active Layer
    const SortableItem = ({ id, layer }) => {
        const {
            attributes,
            listeners,
            setNodeRef,
            transform,
            transition,
        } = useSortable({ id });
        
        const style = {
            transform: CSS.Transform.toString(transform),
            transition,
        };

        return (
            <div 
                ref={setNodeRef} 
                style={style} 
                className="active-layer"
            >
                <div className="layer-drag-handle" 
                    {...attributes} 
                    {...listeners}
                    style={{
                        position: 'absolute',
                        left: '0px',
                        top: '0px',
                        height: '100%',
                        width: '16px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: 'grab',
                        color: '#777',
                        touchAction: 'none'
                    }}
                >
                    ⋮⋮
                </div>
                
                {layer.type === 'mangrove' && (
                    <>
                        <h4>
                            <span style={{ color: '#008000', marginRight: '10px' }}>●</span>
                            Mangrove Coverage ({layer.year})
                        </h4>
                        <p>Showing {layer.districts.length} district(s)</p>
                        <div style={{ display: 'flex', justifyContent: 'center' }}>
                            <button 
                                onClick={() => setActiveCategory('mangrove')}
                                style={{ marginRight: '10px', padding: '3px 8px' }}
                            >
                                Edit
                            </button>
                            <button 
                                onClick={() => clearAllMangroveLayers()}
                                style={{ padding: '3px 8px' }}
                            >
                                Remove All
                            </button>
                        </div>
                    </>
                )}
                
                {layer.type === 'forest' && (
                    <>
                        <h4>
                            <span style={{ color: '#0e5a34', marginRight: '10px' }}>●</span>
                            Forest Coverage ({layer.year})
                        </h4>
                        <p>Showing {layer.districts.length} district(s)</p>
                        <div style={{ display: 'flex', justifyContent: 'center' }}>
                            <button 
                                onClick={() => setActiveCategory('forest')}
                                style={{ marginRight: '10px', padding: '3px 8px' }}
                            >
                                Edit
                            </button>
                            <button 
                                onClick={() => clearAllForestLayers()}
                                style={{ padding: '3px 8px' }}
                            >
                                Remove All
                            </button>
                        </div>
                    </>
                )}
                
                {layer.type === 'cropland' && (
                    <>
                        <h4>
                            <span style={{ color: '#f0e68c', marginRight: '10px' }}>●</span>
                            Cropland Extent ({layer.year})
                        </h4>
                        <p>Showing {layer.districts.length} district(s)</p>
                        <div style={{ display: 'flex', justifyContent: 'center' }}>
                            <button 
                                onClick={() => setActiveCategory('cropland')}
                                style={{ marginRight: '10px', padding: '3px 8px' }}
                            >
                                Edit
                            </button>
                            <button 
                                onClick={() => clearAllCroplandLayers()}
                                style={{ padding: '3px 8px' }}
                            >
                                Remove All
                            </button>
                        </div>
                    </>
                )}
                
                {layer.type === 'peatland' && (
                    <>
                        <h4>
                            <span style={{ color: '#8B4513', marginRight: '10px' }}>●</span>
                            Peatland Distribution ({layer.year})
                        </h4>
                        <div style={{ display: 'flex', justifyContent: 'center' }}>
                            <button 
                                onClick={() => setActiveCategory('peatland')}
                                style={{ marginRight: '10px', padding: '3px 8px' }}
                            >
                                Edit
                            </button>
                            <button 
                                onClick={clearAllPeatlandLayers}
                                style={{ padding: '3px 8px' }}
                            >
                                Remove All
                            </button>
                        </div>
                    </>
                )}
                
                {layer.type === 'fire' && (
                    <>
                        <h4>
                            <span style={{ color: 'red', marginRight: '10px' }}>●</span>
                            Fire Alerts: {layer.date}
                        </h4>
                        <div style={{ display: 'flex', justifyContent: 'center' }}>
                            <button 
                                onClick={() => setActiveCategory('fire')}
                                style={{ marginRight: '10px', padding: '3px 8px' }}
                            >
                                Edit
                            </button>
                            <button 
                                onClick={() => setSelectedDate("")}
                                style={{ padding: '3px 8px' }}
                            >
                                Remove
                            </button>
                        </div>
                    </>
                )}

                {layer.type === 'weather' && (
                    <>
                        <h4>
                            <span style={{ color: '#1976d2', marginRight: '10px' }}>●</span>
                            Weather Data ({layer.location}, {layer.date})
                        </h4>
                        <div style={{ display: 'flex', justifyContent: 'center' }}>
                            <button 
                                onClick={() => setActiveCategory('weather')}
                                style={{ marginRight: '10px', padding: '3px 8px' }}
                            >
                                Edit
                            </button>
                            <button 
                                onClick={() => {
                                    // Remove only the selected marker from weatherMarkers
                                    setWeatherMarkers(prev => prev.filter(
                                        m => !(m.location === layer.location && m.date === layer.date)
                                    ));
                                }}
                                style={{ padding: '3px 8px' }}
                            >
                                Remove
                            </button>
                        </div>
                    </>
                )}
            </div>
        );
    };

    // Render comparison UI in the sidebar
    const renderComparisonUI = (layerType) => {
        let availableYears;
        let availableDistricts;

        if (layerType === 'mangrove') {
            availableYears = ['2010', '2015', '2020'];
            availableDistricts = Object.keys(mangroveToFileMap);
        } else if (layerType === 'forest') {
            availableYears = ['2010', '2015', '2020'];
            availableDistricts = Object.keys(districtToFileMap);
        } else { // Default to cropland
            availableYears = ['2015', '2019'];
            availableDistricts = Object.keys(croplandToFileMap);
        }

        return (
            <div style={{ 
                marginTop: '20px', 
                padding: '15px', 
                backgroundColor: '#e8f5e9', 
                borderRadius: '8px',
                border: '2px solid #4a7c59'
            }}>
                <h4 style={{ marginTop: 0, color: '#4a7c59', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span>📊</span> Compare Between Years
                </h4>
                
                {!comparisonMode ? (
                    <>
                        <div style={{ marginBottom: '15px' }}>
                            <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold', fontSize: '14px' }}>
                                Select District:
                            </label>
                            <select 
                                value={comparisonConfig.district}
                                onChange={(e) => setComparisonConfig({
                                    ...comparisonConfig,
                                    district: e.target.value,
                                    layerType: layerType
                                })}
                                style={{ 
                                    width: '100%', 
                                    padding: '8px', 
                                    borderRadius: '4px',
                                    border: '1px solid #ccc',
                                    fontSize: '14px'
                                }}
                            >
                                <option value="">-- Choose District --</option>
                                {availableDistricts.map(district => (
                                    <option key={district} value={district}>
                                        {district}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div style={{ 
                            display: 'grid', 
                            gridTemplateColumns: '1fr 1fr', 
                            gap: '10px',
                            marginBottom: '15px'
                        }}>
                            <div>
                                <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold', fontSize: '13px', color: '#2196F3' }}>
                                    Left Year:
                                </label>
                                <select 
                                    value={comparisonConfig.leftYear}
                                    onChange={(e) => setComparisonConfig({
                                        ...comparisonConfig,
                                        leftYear: e.target.value
                                    })}
                                    style={{ 
                                        width: '100%', 
                                        padding: '6px',
                                        borderRadius: '4px',
                                        border: '2px solid #2196F3',
                                        fontSize: '13px'
                                    }}
                                >
                                    {availableYears.map(year => (
                                        <option key={year} value={year}>{year}</option>
                                    ))}
                                </select>
                            </div>

                            <div>
                                <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold', fontSize: '13px', color: '#e74c3c' }}>
                                    Right Year:
                                </label>
                                <select 
                                    value={comparisonConfig.rightYear}
                                    onChange={(e) => setComparisonConfig({
                                        ...comparisonConfig,
                                        rightYear: e.target.value
                                    })}
                                    style={{ 
                                        width: '100%', 
                                        padding: '6px',
                                        borderRadius: '4px',
                                        border: '2px solid #e74c3c',
                                        fontSize: '13px'
                                    }}
                                >
                                    {availableYears.map(year => (
                                        <option key={year} value={year}>{year}</option>
                                    ))}
                                </select>
                            </div>
                        </div>

                        <button 
                            onClick={handleActivateComparison}
                            disabled={!comparisonConfig.district}
                            style={{ 
                                width: '100%', 
                                padding: '12px', 
                                backgroundColor: comparisonConfig.district ? '#4a7c59' : '#ccc',
                                color: 'white',
                                border: 'none',
                                borderRadius: '6px',
                                cursor: comparisonConfig.district ? 'pointer' : 'not-allowed',
                                fontWeight: 'bold',
                                fontSize: '16px',
                                transition: 'background-color 0.2s'
                            }}
                        >
                            Start Comparison
                        </button>
                    </>
                ) : (
                    <div style={{ textAlign: 'center' }}>
                        <p style={{ marginBottom: '15px', fontWeight: 'bold', fontSize: '15px' }}>
                            Comparing: <span style={{ color: '#4a7c59' }}>{comparisonConfig.district}</span>
                        </p>
                        <div style={{ 
                            display: 'flex', 
                            justifyContent: 'space-between',
                            marginBottom: '15px',
                            padding: '10px',
                            backgroundColor: 'white',
                            borderRadius: '4px'
                        }}>
                            <span style={{ color: '#2196F3', fontWeight: 'bold' }}>
                                ← {comparisonConfig.leftYear}
                            </span>
                            <span style={{ color: '#e74c3c', fontWeight: 'bold' }}>
                                {comparisonConfig.rightYear} →
                            </span>
                        </div>
                        <button 
                            onClick={handleExitComparison}
                            style={{ 
                                width: '100%', 
                                padding: '12px', 
                                backgroundColor: '#e74c3c',
                                color: 'white',
                                border: 'none',
                                borderRadius: '6px',
                                cursor: 'pointer',
                                fontWeight: 'bold',
                                fontSize: '16px'
                            }}
                        >
                            Exit Comparison Mode
                        </button>
                    </div>
                )}
            </div>
        );
    };

    // renderActiveContent function for Active Layers tab 
    const renderActiveContent = () => {
        // Count the active layers by category
        const activeMangrove = Object.keys(mangroveLayersVisible).filter(d => mangroveLayersVisible[d]).length;
        const activeForest = Object.keys(forestLayersVisible).filter(d => forestLayersVisible[d]).length;
        const activeCropland = Object.keys(croplandLayersVisible).filter(d => croplandLayersVisible[d]).length;
            
        return (
            <div>
                {!isMobile && <h3>Selected Layers</h3>}
                <div style={{ marginBottom: '15px' }}>
                    <p className="drag-instructions" style={{
                        marginBottom: '15px',
                        fontSize: '0.9em',
                        fontStyle: 'italic',
                        color: '#555',
                        backgroundColor: '#eaeaea',
                        padding: '8px 12px',
                        borderRadius: '4px'
                    }}>
                        <span style={{ color: '#4a7c59', marginRight: '5px' }}>💡</span>
                        Drag layers up or down to change their order on the map. Top layers will appear above lower layers.
                    </p>
                    
                    {layerOrder.length > 0 ? (
                    <DndContext 
                        sensors={sensors}
                        collisionDetection={closestCenter}
                        onDragEnd={handleDragEnd}
                    >
                        <SortableContext 
                            items={layerOrder.map(item => item.id)}
                            strategy={verticalListSortingStrategy}
                        >
                            <div className="active-layers-list">
                                {layerOrder.slice().reverse().map((layer) => (
                                    <SortableItem key={layer.id} id={layer.id} layer={layer} />
                                ))}
                            </div>
                        </SortableContext>
                    </DndContext>
                        ) : (
                            <p>No layers are currently active. Select layers from the categories in the sidebar.</p>
                        )}

                    {layerOrder.length > 0 && (
                        <div style={{ marginTop: '15px' }}>
                            <button 
                                onClick={() => {
                                    clearAllMangroveLayers();
                                    clearAllForestLayers();
                                    clearAllCroplandLayers();
                                    clearAllPeatlandLayers();
                                    setSelectedDate("");
                                    setWeatherMarkers([]);
                                }}
                                style={{ 
                                    padding: '8px 12px', 
                                    backgroundColor: '#e74c3c', 
                                    color: 'white', 
                                    border: 'none', 
                                    borderRadius: '4px', 
                                    cursor: 'pointer',
                                    fontWeight: 'bold'
                                }}
                            >
                                Clear All Layers
                            </button>
                        </div>
                    )}
                </div>
            </div>
        );
    };


    // renderContent function to use the new renderActiveContent()
    const renderContent = () => {
        switch (activeCategory) {
            case "active":
                return renderActiveContent(); // renderActiveContent also has an H3
            case "mangrove":
                return (
                    <div>
                        {!isMobile && <h3>Mangrove Coverage - {selectedMangroveYear}</h3>}
                        <div style = {{ marginBottom: '15px' }}>
                            <select 
                                style = {{ padding: '5px', marginRight: '10px' }} 
                                value = {selectedMangroveYear} 
                                onChange = {(e) => setSelectedMangroveYear(e.target.value)}
                            >
                                <option value="2020">2020</option>
                                <option value="2015">2015</option>
                                <option value="2010">2010</option>
                            </select>

                            {/* Info Icon for Mangrove */}
                            <IconButton
                                size="small"
                                onClick={() => setMangroveInfoOpen(!mangroveInfoOpen)}
                                sx={{
                                    color: '#2196F3',
                                    padding: '4px',
                                    '&:hover': {
                                        backgroundColor: 'rgba(33, 150, 243, 0.08)'
                                    }
                                }}
                            >
                                <InfoOutlinedIcon fontSize="small" />
                            </IconButton>
                        </div>

                        {/* Info Panel for Mangrove */}
                        {mangroveInfoOpen && (
                            <div style={{
                                backgroundColor: '#e3f2fd',
                                border: '1px solid #2196F3',
                                borderRadius: '6px',
                                padding: '12px',
                                marginBottom: '10px',
                                fontSize: '13px',
                                lineHeight: '1.5'
                            }}>
                                <div style={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    marginBottom: '8px'
                                }}>
                                    <strong style={{ color: '#1565c0' }}>
                                        How to use
                                    </strong>
                                    <IconButton
                                        size="small"
                                        onClick={() => setMangroveInfoOpen(false)}
                                        sx={{ padding: '2px' }}
                                    >
                                        <CloseIcon fontSize="small" />
                                    </IconButton>
                                </div>
                                <div style={{ color: '#424242' }}>
                                    Choose the districts you want to view the mangrove coverage of by clicking on the checkboxes below. You can also click on the map to view specific coverage statistics for that location.
                                </div>
                            </div>
                        )}

                        <div style={{ opacity: isComparingMangroves ? 0.5 : 1, pointerEvents: isComparingMangroves ? 'none' : 'auto', marginTop: '20px' }}>
                            <div style = {{ marginBottom: '15px' }}>
                                <label>
                                    <input type = "checkbox" checked = {allMangroveVisible} onChange = {handleToggleAllMangroveLayers} />
                                    Toggle All Mangrove Layers
                                </label>
                            </div>
                            <div style = {{ marginBottom: '15px' }}>
                                <label>
                                    <input
                                        type="checkbox"
                                        checked={hideEmptyMangroveDistricts}
                                        onChange={(e) => setHideEmptyMangroveDistricts(e.target.checked)}
                                    />
                                    Hide Districts Without Mangrove
                                </label>
                            </div>
                        </div>
                        
                        {!comparisonMode && Object.entries(divisionData).map(([division, districts]) => {
                            // Filter districts based on checkbox state
                            const visibleDistricts = hideEmptyMangroveDistricts
                                ? districts.filter(d => mangroveToFileMap[d])
                                : districts;

                            // If no districts to show in this division, skip rendering it
                            if (visibleDistricts.length === 0) return null;

                            return (
                                <div key={division} style={{marginBottom: '10px'}}>
                                    <div
                                        onClick={() => toggleExpandedDivisions(division)}
                                        style={{
                                            cursor: 'pointer',
                                            fontweight: 'bold',
                                            background: '#e0e0e0',
                                            padding: '8px',
                                            borderRadius: '4px'
                                        }}
                                    >
                                        {division} {expandedDivisions[division] ? '▾' : '▸'}
                                    </div>
                                    {expandedDivisions[division] && (
                                        <div style={{ paddingLeft: '15px', marginTop: '5px' }}>
                                            {visibleDistricts.map((district) => {
                                                const layerkey = mangroveToFileMap[district];
                                                const hasData = !!layerkey;

                                                return (
                                                    <label key={district} style={{ display: 'block', marginBottom: '5px', opacity: hasData ? 1 : 0.5 }}>
                                                        <input
                                                            type='checkbox'
                                                            checked={!!mangroveLayersVisible[district]}
                                                            disabled={!hasData}
                                                            onChange={() => {
                                                                if (hasData) {
                                                                    toggleMangroveLayer(district);
                                                                    toggleLayer(layerkey);
                                                                }
                                                            }}
                                                        />
                                                        {district}
                                                    </label>
                                                );
                                            })}
                                        </div>
                                    )}
                                </div>
                            );
                        })}

                        {/* COMPARISON UI */}
                        {renderComparisonUI('mangrove')}

                        {/* Data source information for mangrove */}
                        <div style={{ 
                            marginTop: '15px', 
                            padding: '20px', 
                            backgroundColor: '#f0f8ff', 
                            borderRadius: '4px', 
                            border: '1px solid #a4c8e1', 
                            textAlign: 'left'
                        }}>
                            <strong>Data Source:</strong> Global Mangrove Watch (1996 - 2020) Version 3.0 Dataset <br />
                            <strong>Link:</strong> <a href="https://zenodo.org/records/6894273?fbclid=IwZXh0bgNhZW0CMTEAAR5xbKWg349h8WWYQ4LSnIXYYSlYvWY6vMIZxcvOpVdVMFCW89SGtIZKCTWGbw_aem_oNKp5HSQ9GB67bO6bdIt3Q" target="_blank" rel="noopener noreferrer" style={{ color: '#2196F3', textDecoration: 'none' }}>Zenodo</a><br />
                            <strong>Format:</strong> GeoTIFF<br />
                            <strong>Coverage:</strong> Entire Sarawak<br />
                            <strong>Year:</strong> {selectedMangroveYear}
                        </div>
    
                    </div>
                );

            case "forest":
                return (
                    <div>
                        {!isMobile && <h3>Forest Coverage - {selectedForestYear}</h3>}
                        <div style={{ marginBottom: '15px' }}>
                            <select 
                                style={{ padding: '5px', marginRight: '10px' }} 
                                value={selectedForestYear} 
                                onChange={(e) => setSelectedForestYear(e.target.value)}
                            >
                                <option value="2020">2020</option>
                                <option value="2015">2015</option>
                                <option value="2010">2010</option>
                            </select>

                            {/* Info Icon for Forest */}
                            <IconButton
                                size="small"
                                onClick={() => setForestInfoOpen(!forestInfoOpen)}
                                sx={{
                                    color: '#2196F3',
                                    padding: '4px',
                                    '&:hover': {
                                        backgroundColor: 'rgba(33, 150, 243, 0.08)'
                                    }
                                }}
                            >
                                <InfoOutlinedIcon fontSize="small" />
                            </IconButton>
                        </div>

                        {/* Info Panel for Forest */}
                        {forestInfoOpen && (
                            <div style={{
                                backgroundColor: '#e3f2fd',
                                border: '1px solid #2196F3',
                                borderRadius: '6px',
                                padding: '12px',
                                marginBottom: '10px',
                                fontSize: '13px',
                                lineHeight: '1.5'
                            }}>
                                <div style={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    marginBottom: '8px'
                                }}>
                                    <strong style={{ color: '#1565c0' }}>
                                        How to use
                                    </strong>
                                    <IconButton
                                        size="small"
                                        onClick={() => setForestInfoOpen(false)}
                                        sx={{ padding: '2px' }}
                                    >
                                        <CloseIcon fontSize="small" />
                                    </IconButton>
                                </div>
                                <div style={{ color: '#424242' }}>
                                    Choose the districts you want to view the forest coverage of by clicking on the checkboxes below. Click on any colored pixel on the map to see the specific land cover classification.
                                </div>
                            </div>
                        )}
                        <div style = {{ marginBottom: '15px' }}>
                            <label>
                                <input type = "checkbox" checked = {allForestVisible} onChange = {handleToggleAllForestLayers} />
                                Toggle All Forest Layers
                            </label>
                        </div>

                        {!comparisonMode && Object.entries(divisionData).map(([division, districts]) => (
                            <div key={division} style={{marginBottom: '10px'}}>
                                <div
                                    onClick={() => toggleExpandedDivisions(division)}
                                    style={{
                                        cursor: 'pointer',
                                        fontweight: 'bold',
                                        background: '#e0e0e0',
                                        padding: '8px',
                                        borderRadius: '4px'
                                    }}
                                >
                                    {division} {expandedDivisions[division] ? '▾' : '▸'}
                                </div>
                                {expandedDivisions[division] && (
                                    <div style={{ paddingLeft: '15px', marginTop: '5px' }}>
                                        {districts.map((district) => {
                                            const layerkey = districtToFileMap[district];
                                            return (
                                                <label key={district} style={{ display: 'block', marginBottom: '5px' }}>
                                                    <input
                                                        type='checkbox'
                                                        checked={!!forestLayersVisible[district]}
                                                        onChange={() => {
                                                            toggleForestLayer(district);
                                                            toggleLayer(layerkey);
                                                        }}
                                                    />
                                                    {district}
                                                </label>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>
                        ))}

                        {/* COMPARISON UI */}
                        {renderComparisonUI('forest')}

                        {/* Data source information for forest */}
                        <div style={{ 
                            marginTop: '15px', 
                            padding: '20px', 
                            backgroundColor: '#f0f8ff', 
                            borderRadius: '4px', 
                            border: '1px solid #a4c8e1', 
                            textAlign: 'left'
                        }}>
                            <strong>Data Source:</strong> Global Land Cover and Land Use 2000 and 2020 <br />
                            <strong>Link:</strong> <a href="https://storage.googleapis.com/earthenginepartners-hansen/GLCLU2000-2020/v2/download.html?fbclid=IwZXh0bgNhZW0CMTEAAR4MgHCM9fklxsrQqtt4xpzxpB-kxWOAD-c7PdAe3y4Izgq31PauipD0ZStifA_aem_7RhWneqrG5hN1Va4fVHUAQ" target="_blank" rel="noopener noreferrer" style={{ color: '#2196F3', textDecoration: 'none' }}>Global Land Analysis and Discovery</a><br />
                            <strong>Format:</strong> GeoTIFF<br />
                            <strong>Coverage:</strong> Entire Sarawak<br />
                            <strong>Year:</strong> {selectedForestYear}
                        </div>
                    </div>
                );

            case "fire":
                return (
                <div style={{ height: "100%", display: "flex", flexDirection: "column" }}>
                    {/* No top-level H3 here, so this case is fine as is regarding duplication */}
                    <div style={{ padding: "10px", backgroundColor: "#f5f5f5" }}>
                        <label>
                            Select Date:
                            <span style={{ marginLeft: 10, fontWeight: 600 }}>
                                {uniqueDates[sliderDateIndex] || "--"}
                            </span>
                        </label>
                        
                        {/* Calendar Picker */}
                        <div style={{ margin: "10px 0" }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            {/* inline loading indicator */}
                            {fireLoading ? (
                                <span style={{ color: '#666', fontSize: '0.95em', display: 'inline-flex', alignItems: 'center' }}>
                                    <span style={{
                                        width: 10, height: 10, borderRadius: 10,
                                        background: '#1976d2', marginRight: 6,
                                        animation: 'pulse 1s infinite'
                                    }} />
                                    Loading...
                                </span>
                            ) : null}

                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                                <DatePicker
                                    label="Select Date"
                                    value={selectedDate ? dayjs(selectedDate) : null}
                                    onChange={(newValue) => {
                                        setSelectedDate(newValue ? newValue.format('YYYY-MM-DD') : '');
                                        setIsPlaying(false);
                                    }}
                                    disableFuture
                                    minDate={uniqueDates.length ? dayjs(uniqueDates[0]) : undefined}
                                    maxDate={uniqueDates.length ? dayjs(uniqueDates[uniqueDates.length - 1]) : undefined}
                                    shouldDisableDate={(date) => !uniqueDates.includes(date.format('YYYY-MM-DD'))}
                                    renderInput={(params) => <TextField {...params} size="small" sx={{ minWidth: 180, marginRight: 2 }} />}
                                />
                            </LocalizationProvider>
                        </div>
                        <style>{`@keyframes pulse {0% {opacity:1} 50% {opacity:0.4} 100% {opacity:1}}`}</style>
                        </div>

                        
                        <div style={{ display: "flex", alignItems: "center", marginTop: 0 }}>
                            <button
                                onClick={() => setIsPlaying(!isPlaying)}
                                style={{ marginRight: 10 }}
                            >
                                {isPlaying ? "Pause" : "Play"}
                            </button>
                            <input
                                type="range"
                                min={0}
                                max={uniqueDates.length - 1}
                                value={sliderDateIndex}
                                onChange={e => {
                                    setSliderDateIndex(Number(e.target.value));
                                    setSelectedDate(uniqueDates[Number(e.target.value)]);
                                    setIsPlaying(false);
                                }}
                                style={{ flex: 1 }}
                            />
                        </div>

                        <div style={{
                            background: "#fff8e1",
                            borderRadius: 6,
                            padding: "8px 12px",
                            marginBottom: 10,
                            marginTop: 10,
                            fontSize: "0.97em",
                            border: "1px solid #ffe0b2"
                        }}>
                            <b>Fire Alert Statistics</b><br />
                            Total Alerts: <b>{totalAlerts}</b><br />
                            High: <span style={{color: "red"}}>{confidenceCounts.h}</span> &nbsp;
                            Nominal: <span style={{color: "orange"}}>{confidenceCounts.n}</span> &nbsp;
                            Low: <span style={{color: "green"}}>{confidenceCounts.l}</span>
                        </div>

                        {/* --- DOWNLOAD FULL ARCHIVE BUTTON --- */}
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                        <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <span style={{ fontSize: 14 }}>Year:</span>
                            <select
                            value={downloadYear}
                            onChange={e => setDownloadYear(e.target.value)}
                            style={{ padding: '6px 10px', borderRadius: 6 }}
                            >
                            {availableDownloadYears.map(y => (
                                <option key={y} value={y}>{y === 'all' ? 'All' : y}</option>
                            ))}
                            </select>
                        </label>

                        <a
                            href={`${API_BASE_URL}/fires/download?year=${encodeURIComponent(downloadYear)}`}
                            onClick={async (ev) => {
                            // Try server-side download first; if server returns 404, fallback to client-side filtering
                            ev.preventDefault();
                            try {
                                const base = (typeof API_BASE_URL !== 'undefined' && API_BASE_URL) ? API_BASE_URL : (process.env.REACT_APP_API_URL || '');
                                const devFallback = (process.env.NODE_ENV === 'development') ? 'http://localhost:8000' : '';
                                const resolvedBase = (base || devFallback || window.location.origin).replace(/\/+$/, '');
                                const downloadUrl = `${resolvedBase}/fires/download?year=${encodeURIComponent(downloadYear)}`;

                                const headRes = await fetch(downloadUrl, { method: 'HEAD' });
                                if (headRes.ok) {
                                // If HEAD OK, navigate to the URL to trigger browser download
                                window.location.href = downloadUrl;
                                return;
                                }

                                // If HEAD returned 404 or not ok, fallback to client-side approach using combined CSV
                                console.warn('Server-side filtered download unavailable, using client-side fallback.');
                                const combinedUrl = `${resolvedBase}/fires/combined`;
                                const res = await fetch(combinedUrl);
                                if (!res.ok) throw new Error(`Failed to fetch combined archive: ${res.status} ${res.statusText}`);
                                const csvText = await res.text();
                                const parsed = Papa.parse(csvText, { header: true, skipEmptyLines: true });
                                const rows = parsed.data || [];
                                const filtered = (downloadYear && downloadYear !== "all")
                                ? rows.filter(r => (r.acq_date || "").toString().startsWith(downloadYear))
                                : rows;

                                if (!filtered.length) {
                                alert("No records for selected year.");
                                return;
                                }

                                const headers = parsed.meta.fields || Object.keys(filtered[0]);
                                const csvRows = [
                                headers.join(","),
                                ...filtered.map(row => headers.map(h => {
                                    const v = row[h];
                                    if (v === null || v === undefined) return "";
                                    const s = String(v).replace(/"/g, '""');
                                    return s.includes(",") || s.includes('"') || s.includes("\n") ? `"${s}"` : s;
                                }).join(","))
                                ].join("\r\n");

                                const blob = new Blob([csvRows], { type: "text/csv" });
                                const filename = downloadYear === "all" ? 'sarawak_fires_full.csv' : `sarawak_fires_${downloadYear}.csv`;
                                const link = document.createElement('a');
                                link.href = window.URL.createObjectURL(blob);
                                link.download = filename;
                                document.body.appendChild(link);
                                link.click();
                                link.remove();
                                window.URL.revokeObjectURL(link.href);

                            } catch (err) {
                                console.error("Download failed:", err);
                                alert("Download failed. See console for details.");
                            }
                            }}
                            style={{
                            display: 'inline-block',
                            padding: '8px 16px',
                            backgroundColor: '#4a7c59',
                            color: 'white',
                            textDecoration: 'none',
                            borderRadius: '4px',
                            fontWeight: 'bold',
                            fontSize: '14px'
                            }}
                        >
                            Download CSV
                        </a>
                        </div>


                        <div
                            style={{
                                background: "#f8f9fa",
                                borderRadius: 6,
                                padding: "10px 12px",
                                marginBottom: 15,
                                border: "1px solid #eee",
                                height: 120,
                                cursor: "pointer",
                                position: "relative"
                            }}
                            onClick={() => setTrendsDialogOpen(true)}
                            title="Click to expand"
                        >
                            <div style={{ display: "flex", alignItems: "center", marginBottom: 6 }}>
                                <b style={{ fontSize: "0.95em", marginRight: 12 }}>Historical Fire Alert Trends</b>
                                <label style={{ fontSize: "0.95em" }}>
                                    <span style={{ marginRight: 6 }}>Year:</span>
                                    <select
                                        value={trendYear}
                                        onChange={e => setTrendYear(e.target.value)}
                                        style={{ padding: "2px 8px", minWidth: 60 }}
                                    >
                                        <option value="all">All</option>
                                        {availableTrendYears.map(year => (
                                            <option key={year} value={year}>{year}</option>
                                        ))}
                                    </select>
                                </label>
                            </div>
                            <Line
                                data={fireTrendsChartDataFiltered}
                                options={{
                                    responsive: true,
                                    maintainAspectRatio: false,
                                    plugins: {
                                        legend: { display: false },
                                        title: { display: false }
                                    },
                                    scales: {
                                        x: { title: { display: false } },
                                        y: { title: { display: false }, beginAtZero: true }
                                    }
                                }}
                                height={80}
                            />
                            <span style={{
                                position: "absolute",
                                top: 8,
                                right: 12,
                                fontSize: 18,
                                color: "#888"
                            }}>🔍</span>
                        </div>

                        {/*4. In the Dialog for the full-size chart, use the same filter:*/}
                        <Dialog open={trendsDialogOpen} onClose={() => setTrendsDialogOpen(false)} maxWidth="md" fullWidth>
                            <DialogTitle>
                                Historical Fire Alert Trends
                                <IconButton
                                    aria-label="close"
                                    onClick={() => setTrendsDialogOpen(false)}
                                    sx={{
                                        position: 'absolute',
                                        right: 8,
                                        top: 8,
                                        color: (theme) => theme.palette.grey[500],
                                    }}
                                >
                                    <CloseIcon />
                                </IconButton>
                                <span style={{ float: "right", marginLeft: 16 }}>
                                    <label style={{ fontSize: "0.95em" }}>
                                        Year:&nbsp;
                                        <select
                                            value={trendYear}
                                            onChange={e => setTrendYear(e.target.value)}
                                            style={{ padding: "2px 8px", minWidth: 60 }}
                                        >
                                            <option value="all">All</option>
                                            {availableTrendYears.map(year => (
                                                <option key={year} value={year}>{year}</option>
                                            ))}
                                        </select>
                                    </label>
                                </span>
                            </DialogTitle>
                            <DialogContent dividers>
                                <div style={{ height: 400 }}>
                                    <Line
                                        data={fireTrendsChartDataFiltered}
                                        options={{
                                            responsive: true,
                                            maintainAspectRatio: false,
                                            plugins: {
                                                legend: { display: false },
                                                title: { display: false }
                                            },
                                            scales: {
                                                x: { title: { display: true, text: 'Date' } },
                                                y: { title: { display: true, text: 'Number of Alerts' }, beginAtZero: true }
                                            }
                                        }}
                                    />
                                </div>
                            </DialogContent>
                        </Dialog>

                        {/* Data source information for fire */}
                        <div style={{ 
                            marginTop: '40px', 
                            padding: '20px', 
                            backgroundColor: '#f0f8ff', 
                            borderRadius: '4px', 
                            border: '1px solid #a4c8e1', 
                            textAlign: 'left'
                        }}>
                            <strong>Data Source:</strong> Fire Information for Resource Management System <br />
                            <strong>Link:</strong> <a href="https://firms.modaps.eosdis.nasa.gov/" target="_blank" rel="noopener noreferrer" style={{ color: '#2196F3', textDecoration: 'none' }}>NASA FIRMS</a><br />
                            <strong>Satellite:</strong> VIIRS (Visible Infrared Imaging Radiometer Suite)<br />
                            <strong>Coverage:</strong> Entire Sarawak<br />
                            <strong>Year:</strong> Since 2015<br />
                            <strong>Update Frequency:</strong> Daily<br />
                        </div>
                    </div>
                </div>
            );

            case "weather":
                return (
                <div style={{ padding: isMobile ? '0 5px' : '0' }}>
                    {!isMobile && <h3>Weather Data</h3>}
                    <div style={{ marginBottom: '15px' }}>
                       <label>
                           Weather Data Source:{" "}
                           <select
                               value={weatherApiSource}
                               onChange={e => setWeatherApiSource(e.target.value)}
                               style={{ marginBottom: 8, width: "60%" }}
                           >
                               <option value="openweather">OpenWeatherMap</option>
                               <option value="openmeteo">Open-Meteo</option>
                           </select>
                       </label>
                    </div>
                    <div>
                       <label>
                           Location (Sarawak):{" "}
                           <input
                               type="text"
                               value={weatherLocation}
                               onChange={e => setWeatherLocation(e.target.value)}
                               placeholder="e.g. Kuching, Miri, Sibu"
                               style={{ marginBottom: 8, width: "90%" }}
                           />
                       </label>
                    </div>
                    <div>
                       <label>
                           Date:{" "}
                           <input
                               type="date"
                               value={weatherDate}
                               onChange={e => setWeatherDate(e.target.value)}
                               max={new Date().toISOString().split("T")[0]}
                           />
                       </label>
                    </div>
                    {weatherLoading && <p>Loading weather data...</p>}
                    {weatherError && <p style={{ color: "red" }}>{weatherError}</p>}
                    {layerOrder.filter(l => l.type === 'weather').length > 0 && (() => {
                        // Get the topmost weather layer (last in layerOrder, since map is rendered in reverse)
                        const weatherLayers = layerOrder.filter(l => l.type === 'weather');
                        const marker = weatherLayers[weatherLayers.length - 1];
                        return marker.data && marker.data.list && marker.data.list.length > 0 && (
                            <div>
                                <p><b>Date:</b> {marker.date}</p>
                                <p><b>Location:</b> {marker.location}</p>
                                <table style={{ width: "100%", fontSize: "0.95em", marginTop: 10 }}>
                                    <thead>
                                        <tr>
                                            <th>Time</th>
                                            <th>Weather</th>
                                            <th>Temp (°C)</th>
                                            <th>Precip (mm)</th>
                                            <th>Humidity (%)</th>
                                            <th>Wind (m/s)</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {marker.data.list.map((item, idx) => (
                                            <tr key={idx}>
                                                <td>{new Date(item.dt * 1000).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</td>
                                                <td>{item.weather[0].description}</td>
                                                <td>{item.main.temp}</td>
                                                <td>{item.rain?.['1h'] || item.rain?.['3h'] || item.precipitation || 0}</td>
                                                <td>{item.main.humidity}</td>
                                                <td>{item.wind.speed}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        );
                    })()}
                    <div style={{ marginTop: 20, marginBottom: 10, padding: 10, borderRadius: 6 }}>
                        <h4>Monthly Weather Averages</h4>
                        <div style={{ marginBottom: 8 }}>
                            <label>
                                Location:{" "}
                                <input
                                    type="text"
                                    value={weatherRangeLocation}
                                    onChange={e => setWeatherRangeLocation(e.target.value)}
                                    placeholder="e.g. Kuching, Miri, Sibu"
                                    style={{ width: "70%" }}
                                />
                            </label>
                        </div>
                        <div style={{ marginBottom: 8 }}>
                            <label>
                                Start Month:{" "}
                                <input
                                    type="month"
                                    value={weatherRangeStart}
                                    onChange={e => setWeatherRangeStart(e.target.value)}
                                    max={weatherRangeEnd || new Date().toISOString().slice(0, 7)}
                                />
                            </label>
                        </div>
                        <div style={{ marginBottom: 8 }}>
                            <label>
                                End Month:{" "}
                                <input
                                    type="month"
                                    value={weatherRangeEnd}
                                    onChange={e => setWeatherRangeEnd(e.target.value)}
                                    min={weatherRangeStart}
                                    max={new Date().toISOString().slice(0, 7)}
                                />
                            </label>
                        </div>
                        <button
                            onClick={handleWeatherMonthlyAverages}
                            disabled={weatherRangeLoading || !weatherRangeStart || !weatherRangeEnd || !weatherRangeLocation}
                            style={{ padding: "6px 14px", background: "#0bb43eff", color: "white", border: "none", borderRadius: 4, fontWeight: "bold" }}
                        >
                            {weatherRangeLoading ? "Calculating..." : "Calculate Monthly Averages"}
                        </button>
                        {weatherRangeError && <div style={{ color: "red", marginTop: 8 }}>{weatherRangeError}</div>}
                        {weatherMonthlyAverages.length > 0 && (
                        <div style={{ marginTop: 12, padding: 10, borderRadius: 4 }}>
                            <h4>Monthly Weather Averages</h4>
                                                        <button
                                onClick={downloadWeatherAveragesCSV}
                                disabled={!weatherMonthlyAverages.length}
                                style={{
                                    padding: "6px 14px",
                                    background: "#0bb43eff",
                                    color: "white",
                                    border: "none",
                                    borderRadius: 4,
                                    fontWeight: "bold",
                                    marginLeft: 8
                                }}
                            >
                                Download CSV
                            </button>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 24 }}>
                                {/* Temperature Chart */}
                                <div style={{ flex: 1, minWidth: 280, height: 200, position: 'relative' }}>
                                    <div
                                        style={{
                                            position: 'absolute',
                                            top: 8,
                                            right: 12,
                                            zIndex: 2,
                                            cursor: 'pointer',
                                            fontSize: 22,
                                            color: '#1976d2'
                                        }}
                                        title="Enlarge"
                                        onClick={() => setWeatherTempDialogOpen(true)}
                                    >
                                        🔍
                                    </div>
                                    <Line
                                        data={weatherMonthlyTempData}
                                        options={{
                                            responsive: true,
                                            maintainAspectRatio: false,
                                            plugins: { legend: { position: 'top' }, title: { display: true, text: 'Temperature (°C)' } },
                                            scales: { y: { title: { display: true, text: 'Temp (°C)' } } }
                                        }}
                                        height={180}
                                    />
                                    <Dialog open={weatherTempDialogOpen} onClose={() => setWeatherTempDialogOpen(false)} maxWidth="md" fullWidth>
                                        <DialogTitle>
                                            Temperature (°C)
                                            <IconButton
                                                aria-label="close"
                                                onClick={() => setWeatherTempDialogOpen(false)}
                                                sx={{
                                                    position: 'absolute',
                                                    right: 8,
                                                    top: 8,
                                                    color: (theme) => theme.palette.grey[500],
                                                }}
                                            >
                                                <CloseIcon />
                                            </IconButton>
                                        </DialogTitle>
                                        <DialogContent dividers>
                                            <div style={{ height: 400 }}>
                                                <Line
                                                    data={weatherMonthlyTempData}
                                                    options={{
                                                        responsive: true,
                                                        maintainAspectRatio: false,
                                                        plugins: { legend: { position: 'top' }, title: { display: true, text: 'Temperature (°C)' } },
                                                        scales: { y: { title: { display: true, text: 'Temp (°C)' } } }
                                                    }}
                                                    height={350}
                                                />
                                            </div>
                                        </DialogContent>
                                    </Dialog>
                                </div>
                                {/* Precipitation Chart */}
                                <div style={{ flex: 1, minWidth: 280, height: 200, position: 'relative' }}>
                                    <div
                                        style={{
                                            position: 'absolute',
                                            top: 8,
                                            right: 12,
                                            zIndex: 2,
                                            cursor: 'pointer',
                                            fontSize: 22,
                                            color: '#e74c3c'
                                        }}
                                        title="Enlarge"
                                        onClick={() => setWeatherPrecipDialogOpen(true)}
                                    >
                                        🔍
                                    </div>
                                    <Line
                                        data={weatherMonthlyPrecipData}
                                        options={{
                                            responsive: true,
                                            plugins: { legend: { position: 'top' }, title: { display: true, text: 'Precipitation (mm)' } },
                                            scales: { y: { title: { display: true, text: 'Precip (mm)' } } }
                                        }}
                                        height={180}
                                    />
                                    <Dialog open={weatherPrecipDialogOpen} onClose={() => setWeatherPrecipDialogOpen(false)} maxWidth="md" fullWidth>
                                        <DialogTitle>
                                            Precipitation (mm)
                                            <IconButton
                                                aria-label="close"
                                                onClick={() => setWeatherPrecipDialogOpen(false)}
                                                sx={{
                                                    position: 'absolute',
                                                    right: 8,
                                                    top: 8,
                                                    color: (theme) => theme.palette.grey[500],
                                                }}
                                            >
                                                <CloseIcon />
                                            </IconButton>
                                        </DialogTitle>
                                        <DialogContent dividers>
                                            <div style={{ height: 400 }}>
                                                <Line
                                                    data={weatherMonthlyPrecipData}
                                                    options={{
                                                        responsive: true,
                                                        maintainAspectRatio: false,
                                                        plugins: { legend: { position: 'top' }, title: { display: true, text: 'Precipitation (mm)' } },
                                                        scales: { y: { title: { display: true, text: 'Precip (mm)' } } }
                                                    }}
                                                    height={350}
                                                />
                                            </div>
                                        </DialogContent>
                                    </Dialog>
                                </div>
                                {/* Humidity Chart */}
                                <div style={{ flex: 1, minWidth: 280, height: 200, position: 'relative' }}>
                                    <div
                                        style={{
                                            position: 'absolute',
                                            top: 8,
                                            right: 12,
                                            zIndex: 2,
                                            cursor: 'pointer',
                                            fontSize: 22,
                                            color: '#00b894'
                                        }}
                                        title="Enlarge"
                                        onClick={() => setWeatherHumidityDialogOpen(true)}
                                    >
                                        🔍
                                    </div>
                                    <Line
                                        data={weatherMonthlyHumidityData}
                                        options={{
                                            responsive: true,
                                            plugins: { legend: { position: 'top' }, title: { display: true, text: 'Humidity (%)' } },
                                            scales: { y: { title: { display: true, text: 'Humidity (%)' } } }
                                        }}
                                        height={180}
                                    />
                                    <Dialog open={weatherHumidityDialogOpen} onClose={() => setWeatherHumidityDialogOpen(false)} maxWidth="md" fullWidth>
                                        <DialogTitle>
                                            Humidity (%)
                                            <IconButton
                                                aria-label="close"
                                                onClick={() => setWeatherHumidityDialogOpen(false)}
                                                sx={{
                                                    position: 'absolute',
                                                    right: 8,
                                                    top: 8,
                                                    color: (theme) => theme.palette.grey[500],
                                                }}
                                            >
                                                <CloseIcon />
                                            </IconButton>
                                        </DialogTitle>
                                        <DialogContent dividers>
                                            <div style={{ height: 400 }}>
                                                <Line
                                                    data={weatherMonthlyHumidityData}
                                                    options={{
                                                        responsive: true,
                                                        maintainAspectRatio: false,
                                                        plugins: { legend: { position: 'top' }, title: { display: true, text: 'Humidity (%)' } },
                                                        scales: { y: { title: { display: true, text: 'Humidity (%)' } } }
                                                    }}
                                                    height={350}
                                                />
                                            </div>
                                        </DialogContent>
                                    </Dialog>
                                </div>
                                {/* Wind Speed Chart */}
                                <div style={{ flex: 1, minWidth: 280, height: 200, position: 'relative' }}>
                                    <div
                                        style={{
                                            position: 'absolute',
                                            top: 8,
                                            right: 12,
                                            zIndex: 2,
                                            cursor: 'pointer',
                                            fontSize: 22,
                                            color: '#636e72'
                                        }}
                                        title="Enlarge"
                                        onClick={() => setWeatherWindDialogOpen(true)}
                                    >
                                        🔍
                                    </div>
                                    <Line
                                        data={weatherMonthlyWindData}
                                        options={{
                                            responsive: true,
                                            plugins: { legend: { position: 'top' }, title: { display: true, text: 'Wind Speed (m/s)' } },
                                            scales: { y: { title: { display: true, text: 'Wind (m/s)' } } }
                                        }}
                                        height={180}
                                    />
                                    <Dialog open={weatherWindDialogOpen} onClose={() => setWeatherWindDialogOpen(false)} maxWidth="md" fullWidth>
                                        <DialogTitle>
                                            Wind Speed (m/s)
                                            <IconButton
                                                aria-label="close"
                                                onClick={() => setWeatherWindDialogOpen(false)}
                                                sx={{
                                                    position: 'absolute',
                                                    right: 8,
                                                    top: 8,
                                                    color: (theme) => theme.palette.grey[500],
                                                }}
                                            >
                                                <CloseIcon />
                                            </IconButton>
                                        </DialogTitle>
                                        <DialogContent dividers>
                                            <div style={{ height: 400 }}>
                                                <Line
                                                    data={weatherMonthlyWindData}
                                                    options={{
                                                        responsive: true,
                                                        maintainAspectRatio: false,
                                                        plugins: { legend: { position: 'top' }, title: { display: true, text: 'Wind Speed (m/s)' } },
                                                        scales: { y: { title: { display: true, text: 'Wind (m/s)' } } }
                                                    }}
                                                    height={350}
                                                />
                                            </div>
                                        </DialogContent>
                                    </Dialog>
                                </div>
                            </div>
                        </div>
                    )}
                    </div>

                    {/* Data source information for weather */}
                    <div style={{ 
                        marginTop: '40px', 
                        padding: '20px', 
                        backgroundColor: '#f0f8ff', 
                        borderRadius: '4px', 
                        border: '1px solid #a4c8e1', 
                        textAlign: 'left'
                    }}>
                        <strong>Data Source:</strong> OpenWeatherMap & Open-Meteo<br />
                        <strong>Links:</strong><br />
                        <a href="https://openweathermap.org/" target="_blank" rel="noopener noreferrer" style={{ color: '#2196F3', textDecoration: 'none' }}>OpenWeatherMap</a><br />
                        <a href="https://open-meteo.com/" target="_blank" rel="noopener noreferrer" style={{ color: '#2196F3', textDecoration: 'none' }}>Open-Meteo</a><br />
                        <strong>Data Type:</strong> Hourly weather data<br />
                        <strong>Parameters:</strong> Temperature, Weather Conditions, Precipitation, Humidity, Wind Speed<br />
                        <strong>Coverage:</strong> Entire Sarawak<br />
                        <strong>Year:</strong> Since 1940 for OpenMetro and one year history for OpenWeatherMap<br />
                    </div>
                </div>
            );

            case "cropland":
                return (
                    <div>
                        {!isMobile && <h3>Cropland Extent - {selectedCroplandYear}</h3>}
                        
                        <div style={{ marginBottom: '15px' }}>
                            <select 
                                style={{ padding: '5px', marginRight: '10px' }} 
                                value={selectedCroplandYear} 
                                onChange={(e) => {
                                    setSelectedCroplandYear(e.target.value);
                                    // Clear existing layers when changing years
                                    if (e.target.value === "2019") {
                                        Object.keys(cropland2019LayersVisible).forEach(district => {
                                            if (cropland2019LayersVisible[district]) {
                                                toggleCropland2019Layer(district);
                                                toggleLayer(cropland2019ToFileMap[district]);
                                            }
                                        });
                                    } else {
                                        Object.keys(croplandLayersVisible).forEach(district => {
                                            if (croplandLayersVisible[district]) {
                                                toggleCroplandLayer(district);
                                                toggleLayer(croplandToFileMap[district]);
                                            }
                                        });
                                    }
                                }}
                            >
                                <option value="2019">2019 (Oil Palm)</option>
                                <option value="2015">2015 (Permanent Crops)</option>
                            </select>

                            {/* Info Icon */}
                            <IconButton 
                                size="small"
                                onClick={() => setCroplandInfoOpen(!croplandInfoOpen)}
                                sx={{ 
                                    color: '#2196F3',
                                    padding: '4px',
                                    '&:hover': {
                                        backgroundColor: 'rgba(33, 150, 243, 0.08)'
                                    }
                                }}
                            >
                                <InfoOutlinedIcon fontSize="small" />
                            </IconButton>
                        </div>

                        {/* Info Panel - appears below the select/icon */}
                        {croplandInfoOpen && (
                            <div style={{
                                backgroundColor: '#e3f2fd',
                                border: '1px solid #2196F3',
                                borderRadius: '6px',
                                padding: '12px',
                                marginBottom: '10px',
                                fontSize: '13px',
                                lineHeight: '1.5'
                            }}>
                                <div style={{ 
                                    display: 'flex', 
                                    justifyContent: 'space-between', 
                                    alignItems: 'center',
                                    marginBottom: '8px'
                                }}>
                                    <strong style={{ color: '#1565c0' }}>
                                        {selectedCroplandYear === "2019" ? "Oil Palm Concessions" : "Permanent Crops"}
                                    </strong>
                                    <IconButton 
                                        size="small" 
                                        onClick={() => setCroplandInfoOpen(false)}
                                        sx={{ padding: '2px' }}
                                    >
                                        <CloseIcon fontSize="small" />
                                    </IconButton>
                                </div>
                                <div style={{ color: '#424242' }}>
                                    {selectedCroplandYear === "2019" 
                                        ? "These are designated areas allocated to companies for oil palm cultivation. Oil palm is a major driver of agriculture in Sarawak, used primarily for palm oil production. These concessions represent commercial plantation boundaries."
                                        : "Areas where crops occupy the land for long periods and do not need to be replanted after each harvest. This includes perennial crops like oil palm, rubber, fruit trees, and other tree crops that are characteristic of Sarawak's agricultural landscape."
                                    }
                                </div>
                            </div>
                        )}

                        {selectedCroplandYear === "2019" ? (
                            // 2019 - District-wise GeoJSON layers
                            <div>
                                <div style={{ marginBottom: '15px' }}>
                                    <label>
                                        <input type="checkbox" checked={allCropland2019Visible} onChange={handleToggleAllCropland2019Layers} />
                                        Toggle All Oil Palm Layers
                                    </label>
                                </div>

                                {/* hide empty districts checkbox for 2019 */}
                                <div style={{ marginBottom: '15px' }}>
                                    <label>
                                        <input
                                            type="checkbox"
                                            checked={hideEmptyCroplandDistricts}
                                            onChange={(e) => setHideEmptyCroplandDistricts(e.target.checked)}
                                        />
                                        Hide Districts Without Cropland
                                    </label>
                                </div>
                                
                                {!comparisonMode && Object.entries(divisionData).map(([division, districts]) => {
                                    // Filter districts based on checkbox state for 2019
                                    const filteredDistricts = hideEmptyCroplandDistricts
                                        ? districts.filter(d => cropland2019AvailableDistricts.has(d))
                                        : districts;

                                    // If no districts to show in this division, skip rendering it
                                    if (filteredDistricts.length === 0) return null;

                                    return (
                                        <div key={division} style={{marginBottom: '10px'}}>
                                            <div
                                                onClick={() => toggleExpandedDivisions(division)}
                                                style={{
                                                    cursor: 'pointer',
                                                    fontWeight: 'bold',
                                                    background: '#e0e0e0',
                                                    padding: '8px',
                                                    borderRadius: '4px'
                                                }}
                                            >
                                                {division} {expandedDivisions[division] ? '▾' : '▸'}
                                            </div>
                                            {expandedDivisions[division] && (
                                                <div style={{ paddingLeft: '15px', marginTop: '5px' }}>
                                                    {districts.map((district) => {
                                                        const hasData = cropland2019AvailableDistricts.has(district);
                                                        // Skip entirely when hideEmpty is on
                                                        if (hideEmptyCroplandDistricts && !hasData) return null;

                                                        return (
                                                            <label
                                                                key={district}
                                                                style={{
                                                                    display: 'block',
                                                                    marginBottom: '5px',
                                                                    opacity: hasData ? 1 : 0.5
                                                                }}
                                                            >
                                                                <input
                                                                    type='checkbox'
                                                                    checked={!!cropland2019LayersVisible[district]}
                                                                    disabled={!hasData}
                                                                    onChange={() => {
                                                                        if (!hasData) return;
                                                                        toggleCropland2019Layer(district);
                                                                        toggleLayer(cropland2019ToFileMap[district]);
                                                                    }}
                                                                />
                                                                {district}
                                                                {!hasData}
                                                            </label>
                                                        );
                                                    })}
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}

                                {/* COMPARISON UI */}
                                {renderComparisonUI('cropland')}
                                
                                {/* Data source info */}
                                <div style={{ 
                                    marginTop: '15px', 
                                    padding: '20px', 
                                    backgroundColor: '#f0f8ff', 
                                    borderRadius: '4px', 
                                    border: '1px solid #a4c8e1', 
                                    textAlign: 'left'
                                }}>
                                    <strong>Data Source:</strong> Sarawak Oil Palm Concessions<br />
                                    <strong>Link:</strong> <a href="https://data.globalforestwatch.org/datasets/gfw::sarawak-oil-palm-concessions/about" target="_blank" rel="noopener noreferrer" style={{ color: '#2196F3', textDecoration: 'none' }}>Global Forest Watch (GFW)</a><br />
                                    <strong>Format:</strong> GeoJSON<br />
                                    <strong>Coverage:</strong> Entire Sarawak<br />
                                    <strong>Data Type:</strong> Oil Palm Concession Boundaries<br />
                                    <strong>Year:</strong> 2019
                                </div>
                            </div>
                        ) : (
                            // 2015 - District-wise layers 
                            <div>
                                <div style={{ marginBottom: '15px' }}>
                                    <label>
                                        <input type="checkbox" checked={allCroplandVisible} onChange={handleToggleAllCroplandLayers} />
                                        Toggle All Cropland Layers
                                    </label>
                                </div>

                                {/* Add hide empty districts checkbox for 2015 */}
                                <div style={{ marginBottom: '15px' }}>
                                    <label>
                                        <input
                                            type="checkbox"
                                            checked={hideEmptyCroplandDistricts}
                                            onChange={(e) => setHideEmptyCroplandDistricts(e.target.checked)}
                                        />
                                        Hide Districts Without Cropland
                                    </label>
                                </div>
                                
                                {!comparisonMode && Object.entries(divisionData).map(([division, districts]) => {
                                    // Filter districts based on checkbox state for 2015
                                    const visibleDistricts = hideEmptyCroplandDistricts
                                        ? districts.filter(d => {
                                            const key = croplandToFileMap[d];
                                            return key && tifFiles["2015"] && tifFiles["2015"][key];
                                        })
                                        : districts;

                                    // If no districts to show in this division, skip rendering it
                                    if (visibleDistricts.length === 0) return null;

                                    return (
                                        <div key={division} style={{marginBottom: '10px'}}>
                                            <div
                                                onClick={() => toggleExpandedDivisions(division)}
                                                style={{
                                                    cursor: 'pointer',
                                                    fontWeight: 'bold',
                                                    background: '#e0e0e0',
                                                    padding: '8px',
                                                    borderRadius: '4px'
                                                }}
                                            >
                                                {division} {expandedDivisions[division] ? '▾' : '▸'}
                                            </div>
                                            {expandedDivisions[division] && (
                                                <div style={{ paddingLeft: '15px', marginTop: '5px' }}>
                                                    {visibleDistricts.map((district) => {
                                                        const layerkey = croplandToFileMap[district];
                                                        return (
                                                            <label key={district} style={{ display: 'block', marginBottom: '5px' }}>
                                                                <input
                                                                    type='checkbox'
                                                                    checked={!!croplandLayersVisible[district]}
                                                                    onChange={() => {
                                                                        toggleCroplandLayer(district);
                                                                        toggleLayer(layerkey);
                                                                    }}
                                                                />
                                                                {district}
                                                            </label>
                                                        );
                                                    })}
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}

                                {/* COMPARISON UI */}
                                {renderComparisonUI('cropland')}
                                
                                {/* Data source info */}
                                <div style={{ 
                                    marginTop: '15px', 
                                    padding: '20px', 
                                    backgroundColor: '#f0f8ff', 
                                    borderRadius: '4px', 
                                    border: '1px solid #a4c8e1', 
                                    textAlign: 'left'
                                }}>
                                    <strong>Data Source:</strong> Global Food Security-support Analysis Data (GFSAD) Cropland Extent 2015 <br />
                                    <strong>Link:</strong> <a href="https://www.earthdata.nasa.gov/data/catalog/lpcloud-gfsad30seace-001" target="_blank" rel="noopener noreferrer" style={{ color: '#2196F3', textDecoration: 'none' }}>NASA Earthdata</a><br />
                                    <strong>Format:</strong> GeoTIFF<br />
                                    <strong>Coverage:</strong> Entire Sarawak<br />
                                    <strong>Data Type:</strong> Permanent Cropland Classification<br />
                                    <strong>Resolution:</strong> 30m<br />
                                    <strong>Year:</strong> 2015
                                </div>
                            </div>
                        )}
                    </div>
                );

            case "peatland":
                return (
                    <div>
                        {!isMobile && <h3>Peatland Distribution - 2019</h3>}
                        
                        {/* Centered header row (matches other tabs) */}
                        <div
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: '8px',
                                marginBottom: '15px'
                            }}
                        >
                            <label style={{ margin: 0 }}>
                                <input
                                    type="checkbox"
                                    checked={allPeatlandVisible}
                                    onChange={handleToggleAllPeatlandLayers}
                                />
                                Toggle All Peatland Layers
                            </label>

                            <IconButton
                                size="small"
                                onClick={() => setPeatlandInfoOpen(!peatlandInfoOpen)}
                                sx={{
                                    color: '#2196F3',
                                    padding: '4px',
                                    '&:hover': { backgroundColor: 'rgba(33, 150, 243, 0.08)' }
                                }}
                            >
                                <InfoOutlinedIcon fontSize="small" />
                            </IconButton>
                        </div>

                        {/* Info Panel */}
                        {peatlandInfoOpen && (
                            <div style={{
                                backgroundColor: '#e3f2fd',
                                border: '1px solid #2196F3',
                                borderRadius: '6px',
                                padding: '12px',
                                marginBottom: '10px',
                                fontSize: '13px',
                                lineHeight: '1.5'
                            }}>
                                <div style={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    marginBottom: '8px'
                                }}>
                                    <strong style={{ color: '#1565c0' }}>How to use</strong>
                                    <IconButton
                                        size="small"
                                        onClick={() => setPeatlandInfoOpen(false)}
                                        sx={{ padding: '2px' }}
                                    >
                                        <CloseIcon fontSize="small" />
                                    </IconButton>
                                </div>
                                <div style={{ color: '#424242' }}>
                                    Choose the districts you want to view the peatland distribution of by clicking on the checkboxes below.
                                </div>
                            </div>
                        )}

                        {/* Hide empty districts checkbox (capitalised) */}
                        <div style={{ marginBottom: '15px' }}>
                            <label>
                                <input
                                    type="checkbox"
                                    checked={hideEmptyPeatlandDistricts}
                                    onChange={(e) => setHideEmptyPeatlandDistricts(e.target.checked)}
                                />
                                Hide Districts Without Peatland
                            </label>
                        </div>

                        {Object.entries(divisionData).map(([division, districts]) => {
                            // Show all by default; hide only when toggled
                            const visibleDistricts = hideEmptyPeatlandDistricts
                                ? districts.filter((d) => peatlandAvailableDistricts.has(d))
                                : districts;

                            if (visibleDistricts.length === 0) return null;

                            return (
                                <div key={division} style={{ marginBottom: '10px' }}>
                                    <div
                                        onClick={() => toggleExpandedDivisions(division)}
                                        style={{
                                            cursor: 'pointer',
                                            fontWeight: 'bold',
                                            background: '#e0e0e0',
                                            padding: '8px',
                                            borderRadius: '4px'
                                        }}
                                    >
                                        {division} {expandedDivisions[division] ? '▾' : '▸'}
                                    </div>

                                    {expandedDivisions[division] && (
                                        <div style={{ paddingLeft: '15px', marginTop: '5px' }}>
                                            {visibleDistricts.map((district) => {
                                                const layerkey = peatlandToFileMap[district];
                                                const hasData = peatlandAvailableDistricts.has(district);

                                                return (
                                                    <label
                                                        key={district}
                                                        style={{
                                                            display: 'block',
                                                            marginBottom: '5px',
                                                            opacity: hasData ? 1 : 0.5 // grey out like mangrove
                                                        }}
                                                    >
                                                        <input
                                                            type="checkbox"
                                                            checked={!!peatlandLayersVisible[district]}
                                                            disabled={!hasData} // disable when no data
                                                            onChange={() => {
                                                                if (!hasData) return;
                                                                togglePeatlandLayer(district);
                                                                if (layerkey) toggleLayer(layerkey);
                                                            }}
                                                        />
                                                        {district}
                                                    </label>
                                                );
                                            })}
                                        </div>
                                    )}
                                </div>
                            );
                        })}

                        {/* Peatland data source info*/}
                        <div style={{ 
                            marginTop: '15px', 
                            padding: '20px', 
                            backgroundColor: '#f0f8ff', 
                            borderRadius: '4px', 
                            border: '1px solid #a4c8e1',
                            textAlign: 'left'
                        }}>
                            <strong>Data Source:</strong> Malaysia Peatland Mapping<br />
                            <strong>Link:</strong> <a href="https://data.globalforestwatch.org/datasets/8d8462fca7b74b298598490b85d3bd44_9/explore?location=1.715287%2C113.861176%2C7.18" target="_blank" rel="noopener noreferrer" style={{ color: '#2196F3', textDecoration: 'none' }}> Global Forest Watch (GFW)</a><br />
                            <strong>Format:</strong> GeoJSON<br />
                            <strong>Coverage:</strong> Entire Sarawak<br />
                            <strong>Data Type:</strong> Peatland Boundary Mapping<br />
                            <strong>Year:</strong> 2019
                        </div>
                    </div>
                );
                
            case "risk":
            return (
                <div style={{ paddingBottom: '30px' }}>
                    {!isMobile && <h3>Risk Analysis</h3>}
                    
                    {/* Tab Selection */}
                    <div style={{ 
                        display: 'flex', 
                        marginBottom: '20px',
                        borderBottom: '1px solid #ccc'
                    }}>
                        {/* <button
                            onClick={() => setRiskTab('forest')}
                            style={{
                                flex: 1,
                                padding: '10px',
                                border: 'none',
                                backgroundColor: riskTab === 'forest' ? '#e74c3c' : 'transparent',
                                color: riskTab === 'forest' ? 'white' : '#333',
                                cursor: 'pointer',
                                fontWeight: 'bold',
                                borderBottom: riskTab === 'forest' ? '3px solid #c0392b' : '3px solid transparent'
                            }}
                        >
                            🌳 Forest Extent Prediction
                        </button> 
                        <button
                            onClick={() => setRiskTab('fire')}
                            style={{
                                flex: 1,
                                padding: '10px',
                                border: 'none',
                                backgroundColor: riskTab === 'fire' ? '#e74c3c' : 'transparent',
                                color: riskTab === 'fire' ? 'white' : '#333',
                                cursor: 'pointer',
                                fontWeight: 'bold',
                                borderBottom: riskTab === 'fire' ? '3px solid #c0392b' : '3px solid transparent'
                            }}
                        >
                            🔥 Fire Alert Forecasting
                        </button> */}
                    </div>

                    {/* Forest Extent Prediction Tab */}
                    {riskTab === 'forest' && (
                        <div>
                            <p>Enter environmental parameters to predict tree cover extent:</p>
                            
                            <div style={{ marginTop: '15px' }}>
                                <div style={{ 
                                    display: 'grid', 
                                    gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
                                    gap: '12px',
                                    columnGap: '15px'
                                }}>
                                    
                                    <div>
                                        <label style={{ display: 'block', marginBottom: '4px', fontWeight: 'bold', fontSize: '15px', minHeight: '32px', lineHeight: '16px' }}>
                                            Slope (degrees):
                                        </label>
                                        <input
                                           
                                            type="number"
                                            step="0.01"
                                            placeholder="e.g. 10.27"
                                        />
                                    </div>

                                    <div>
                                        <label style={{ display: 'block', marginBottom: '4px', fontWeight: 'bold', fontSize: '15px', minHeight: '32px', lineHeight: '16px' }}>
                                            Elevation (m):
                                        </label>
                                        <input
                                           
                                            type="number"
                                            step="0.01"
                                            value={riskInputs.elevation}
                                            onChange={(e) => setRiskInputs({...riskInputs, elevation: e.target.value})}
                                            style={{ width: '100%', padding: '6px', borderRadius: '4px', border: '1px solid #ccc' }}
                                            placeholder="e.g. 119.12"
                                        />
                                    </div>

                                    <div>
                                        <label style={{ display: 'block', marginBottom: '4px', fontWeight: 'bold', fontSize: '15px', minHeight: '32px', lineHeight: '16px' }}>
                                            Precipitation (mm):
                                        </label>
                                        <input
                                            type="number"
                                            step="0.01"
                                            value={riskInputs.precipitation}
                                            onChange={(e) => setRiskInputs({...riskInputs, precipitation: e.target.value})}
                                            style={{ width: '100%', padding: '6px', borderRadius: '4px', border: '1px solid #ccc' }}
                                            placeholder="e.g., 317.35"
                                        />
                                    </div>

                                    <div>
                                        <label style={{ display: 'block', marginBottom: '4px', fontWeight: 'bold', fontSize: '15px', minHeight: '32px', lineHeight: '16px' }}>
                                            Temperature (°C):
                                        </label>
                                        <input
                                            type="number"
                                            step="0.01"
                                            value={riskInputs.temperature}
                                            onChange={(e) => setRiskInputs({...riskInputs, temperature: e.target.value})}
                                            style={{ width: '100%', padding: '6px', borderRadius: '4px', border: '1px solid #ccc' }}
                                            placeholder="e.g., 26.19"
                                        />
                                    </div>

                                    <div>
                                        <label style={{ display: 'block', marginBottom: '4px', fontWeight: 'bold', fontSize: '15px', minHeight: '32px', lineHeight: '16px' }}>
                                            Crop Suitability Index:
                                        </label>
                                        <input
                                            type="number"
                                            step="0.01"
                                            value={riskInputs.cropSuitability}
                                            onChange={(e) => setRiskInputs({...riskInputs, cropSuitability: e.target.value})}
                                            style={{ width: '100%', padding: '6px', borderRadius: '4px', border: '1px solid #ccc' }}
                                            placeholder="e.g. 2.33"
                                        />
                                    </div>

                                    <div>
                                        <label style={{ display: 'block', marginBottom: '4px', fontWeight: 'bold', fontSize: '15px', minHeight: '32px', lineHeight: '16px' }}>
                                            Distance to Urban (m):
                                        </label>
                                        <input
                                            type="number"
                                            step="0.01"
                                            value={riskInputs.distanceUrban}
                                            onChange={(e) => setRiskInputs({...riskInputs, distanceUrban: e.target.value})}
                                            style={{ width: '100%', padding: '6px', borderRadius: '4px', border: '1px solid #ccc' }}
                                            placeholder="e.g. 4472.14"
                                        />
                                    </div>

                                    <div>
                                        <label style={{ display: 'block', marginBottom: '4px', fontWeight: 'bold', fontSize: '15px', minHeight: '32px', lineHeight: '16px' }}>
                                            Distance to Closest Airport (m):
                                        </label>
                                        <input
                                            type="number"
                                            step="0.01"
                                            value={riskInputs.distanceAirport}
                                            onChange={(e) => setRiskInputs({...riskInputs, distanceAirport: e.target.value})}
                                            style={{ width: '100%', padding: '6px', borderRadius: '4px', border: '1px solid #ccc' }}
                                            placeholder="e.g. 27471.81"
                                        />
                                    </div>

                                    <div>
                                        <label style={{ display: 'block', marginBottom: '4px', fontWeight: 'bold', fontSize: '15px', minHeight: '32px', lineHeight: '16px' }}>
                                            Irrigation Area (hectares):
                                        </label>
                                        <input
                                            type="number"
                                            step="0.01"
                                            value={riskInputs.irrigationArea}
                                            onChange={(e) => setRiskInputs({...riskInputs, irrigationArea: e.target.value})}
                                            style={{ width: '100%', padding: '6px', borderRadius: '4px', border: '1px solid #ccc' }}
                                            placeholder="e.g. 58.01"
                                        />
                                    </div>

                                    <div>
                                        <label style={{ display: 'block', marginBottom: '4px', fontWeight: 'bold', fontSize: '15px', minHeight: '32px', lineHeight: '16px' }}>
                                            Soil Drainage:
                                        </label>
                                        <input
                                            type="number"
                                            step="0.01"
                                            value={riskInputs.soilDrainage}
                                            onChange={(e) => setRiskInputs({...riskInputs, soilDrainage: e.target.value})}
                                            style={{ width: '100%', padding: '6px', borderRadius: '4px', border: '1px solid #ccc' }}
                                            placeholder="e.g. 2.25"
                                        />
                                    </div>

                                    <div>
                                        <label style={{ display: 'block', marginBottom: '4px', fontWeight: 'bold', fontSize: '15px', minHeight: '32px', lineHeight: '16px' }}>
                                            Human Influence Index:
                                        </label>
                                        <input
                                            type="number"
                                            step="0.01"
                                            value={riskInputs.humanInfluence}
                                            onChange={(e) => setRiskInputs({...riskInputs, humanInfluence: e.target.value})}
                                            style={{ width: '100%', padding: '6px', borderRadius: '4px', border: '1px solid #ccc' }}
                                            placeholder="e.g. 937.87"
                                        />
                                    </div>

                                    <div>
                                        <label style={{ display: 'block', marginBottom: '4px', fontWeight: 'bold', fontSize: '15px', minHeight: '32px', lineHeight: '16px' }}>
                                            Population (2000):
                                        </label>
                                        <input
                                            type="number"
                                            value={riskInputs.population}
                                            onChange={(e) => setRiskInputs({...riskInputs, population: e.target.value})}
                                            style={{ width: '100%', padding: '6px', borderRadius: '4px', border: '1px solid #ccc' }}
                                            placeholder="e.g. 43190"
                                        />
                                    </div>

                                    <div>
                                        <label style={{ display: 'block', marginBottom: '4px', fontWeight: 'bold', fontSize: '15px', minHeight: '32px', lineHeight: '16px' }}>
                                            Biomass Stock (Mg):
                                        </label>
                                        <input
                                            type="number"
                                            step="0.01"
                                            value={riskInputs.biomassStock}
                                            onChange={(e) => setRiskInputs({...riskInputs, biomassStock: e.target.value})}
                                            style={{ width: '100%', padding: '6px', borderRadius: '4px', border: '1px solid #ccc' }}
                                            placeholder="e.g. 21656445.4"
                                        />
                                    </div>

                                    {/* Ecoregion spans both columns */}
                                    <div style={{ gridColumn: isMobile ? '1' : '1 / -1' }}>
                                        <label style={{ display: 'block', marginBottom: '4px', fontWeight: 'bold' }}>
                                            Ecoregion:
                                        </label>
                                        <select
                                            value={riskInputs.ecoregion}
                                            onChange={(e) => setRiskInputs({...riskInputs, ecoregion: e.target.value})}
                                            style={{ width: '100%', padding: '6px', borderRadius: '4px', border: '1px solid #ccc' }}
                                        >
                                            <option value="borneo">Borneo Lowland and Montane Forests</option>
                                            <option value="mangroves">Greater Sundas Mangroves</option>
                                        </select>
                                    </div>
                                </div>

                                <button
                                    onClick={handleRiskPrediction}
                                    disabled={predictionLoading}
                                    style={{
                                        width: '100%',
                                        padding: '12px',
                                        marginTop: '20px',
                                        backgroundColor: predictionLoading ? '#ccc' : '#e74c3c',
                                        color: 'white',
                                        border: 'none',
                                        borderRadius: '4px',
                                        cursor: predictionLoading ? 'not-allowed' : 'pointer',
                                        fontWeight: 'bold'
                                    }}
                                >
                                    {predictionLoading ? 'Predicting...' : 'Predict Tree Cover Extent'}
                                </button>

                                {/* Error and result displays */}
                                {predictionError && (
                                    <div style={{
                                        marginTop: '15px',
                                        padding: '10px',
                                        backgroundColor: '#ffebee',
                                        border: '1px solid #f44336',
                                        borderRadius: '4px',
                                        color: '#d32f2f'
                                    }}>
                                        <strong>Error:</strong> {predictionError}
                                    </div>
                                )}

                                {riskPrediction !== null && (
                                    <div style={{
                                        marginTop: '15px',
                                        marginBottom: '20px',
                                        padding: '15px',
                                        backgroundColor: '#e8f5e8',
                                        border: '1px solid #4caf50',
                                        borderRadius: '4px',
                                        textAlign: 'center'
                                    }}>
                                        <h4 style={{ margin: '0 0 10px 0', color: '#2e7d32' }}>
                                            🌳 Prediction Result
                                        </h4>
                                        <p style={{ margin: 0, fontSize: '18px', fontWeight: 'bold', color: '#1b5e20' }}>
                                            Predicted Tree Cover Extent: {riskPrediction.toFixed(2)} hectares
                                        </p>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Fire Alert Forecasting Tab */}
                    {riskTab === 'fire' && (
                        <div>
                            <p>Select the time horizon and model for fire alert forecasting:</p>
                            
                            <div style={{ marginTop: '15px' }}>
                                <div style={{ marginBottom: '20px' }}>
                                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', fontSize: '16px' }}>
                                        Forecasting Model:
                                    </label>
                                    <select
                                        value={selectedFireModel}
                                        onChange={(e) => setSelectedFireModel(e.target.value)}
                                        style={{
                                            width: '100%',
                                            padding: '10px',
                                            borderRadius: '4px',
                                            border: '1px solid #ccc',
                                            fontSize: '16px',
                                            marginBottom: '15px'
                                        }}
                                    >
                                        <option value="XGBoost">XGBoost Model</option>
                                        <option value="prophet">Prophet Model</option>
                                    </select>

                                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', fontSize: '16px' }}>
                                        Forecast Time Step (months):
                                    </label>
                                    <input
                                        type="number"
                                        min="1"
                                        max="60"
                                        value={fireInputs.timeStep}
                                        onChange={(e) => setFireInputs({...fireInputs, timeStep: e.target.value})}
                                        style={{ 
                                            width: '100%', 
                                            padding: '10px', 
                                            borderRadius: '4px', 
                                            border: '1px solid #ccc',
                                            fontSize: '16px',
                                            boxSizing: 'border-box'
                                        }}
                                        placeholder="e.g. 12 (for 12 months ahead)"
                                    />
                                    <small style={{ color: '#666', fontSize: '14px' }}>
                                        Enter the number of months ahead you want to forecast (1-60 months)
                                    </small>
                                </div>

                                <button
                                    onClick={handleFirePrediction}
                                    disabled={firePredictionLoading}
                                    style={{
                                        width: '100%',
                                        padding: '12px',
                                        marginTop: '10px',
                                        backgroundColor: firePredictionLoading ? '#ccc' : '#e74c3c',
                                        color: 'white',
                                        border: 'none',
                                        borderRadius: '4px',
                                        cursor: firePredictionLoading ? 'not-allowed' : 'pointer',
                                        fontWeight: 'bold',
                                        fontSize: '16px'
                                    }}
                                >
                                    {firePredictionLoading ? 'Forecasting...' : 'Generate Fire Alert Forecast'}
                                </button>

                                {firePredictionError && (
                                    <div style={{
                                        marginTop: '15px',
                                        padding: '10px',
                                        backgroundColor: '#ffebee',
                                        border: '1px solid #f44336',
                                        borderRadius: '4px',
                                        color: '#d32f2f'
                                    }}>
                                        <strong>Error:</strong> {firePredictionError}
                                    </div>
                                )}

                                {/* Conditional rendering based on selected model */}
                                {firePrediction && selectedFireModel === 'XGBoost' && (
                                    <div style={{ marginTop: '20px' }}>
                                        <h4>Forecast Results ({firePrediction.model_type})</h4>
                                        <p>Total predicted fire alerts over {firePrediction.timestep} months: <strong>{Math.round(firePrediction.total)}</strong></p>

                                        {/* --- CHART 1: Forecast Trend --- */}
                                        <div style={{ height: '300px', marginBottom: '40px' }}>
                                            <Line
                                                options={{
                                                    responsive: true,
                                                    maintainAspectRatio: false,
                                                    plugins: {
                                                        legend: { position: 'top' },
                                                        title: { display: true, text: 'Fire Alert Forecast Trend', font: { size: 16 } }
                                                    },
                                                    scales: {
                                                        y: {
                                                            beginAtZero: true,
                                                            title: { display: true, text: 'Predicted Fire Alerts' }
                                                        }
                                                    }
                                                }}
                                                data={{
                                                    labels: firePrediction.forecast.map(d => new Date(d.month).toLocaleString('default', { month: 'short', year: 'numeric' })),
                                                    datasets: [{
                                                        label: 'Predicted Fire Alerts',
                                                        data: firePrediction.forecast.map(d => d.predicted_fire_count),
                                                        borderColor: 'rgb(255, 99, 132)',
                                                        backgroundColor: 'rgba(255, 99, 132, 0.5)',
                                                        fill: true,
                                                    }]
                                                }}
                                            />
                                        </div>

                                        {/* --- CHART 2: Feature Importance (Only for XGBoost) --- */}
                                        {firePrediction.feature_importances && (
                                            <div style={{ height: '400px', marginBottom: '40px' }}>
                                                <Bar
                                                    options={{
                                                        responsive: true,
                                                        maintainAspectRatio: false,
                                                        indexAxis: 'y',
                                                        plugins: {
                                                            legend: { position: 'top' },
                                                            title: { display: true, text: 'Model Feature Importance', font: { size: 16 } }
                                                        },
                                                        scales: {
                                                            x: {
                                                                beginAtZero: true,
                                                                title: { display: true, text: 'Importance Score' }
                                                            },
                                                            y: {
                                                                ticks: {
                                                                    font: {
                                                                        size: 10
                                                                    }
                                                                }
                                                            }
                                                        }
                                                    }}
                                                    data={{
                                                        labels: firePrediction.feature_importances.slice(0, 10).reverse().map(d => d.feature),
                                                        datasets: [{
                                                            label: 'XGBoost Feature Importance',
                                                            data: firePrediction.feature_importances.slice(0, 10).reverse().map(d => d.importance),
                                                            backgroundColor: 'rgba(54, 162, 235, 0.6)',
                                                            borderColor: 'rgba(54, 162, 235, 1)',
                                                            borderWidth: 1,
                                                        }]
                                                    }}
                                                />
                                            </div>
                                        )}

                                        {/* --- Legend for Feature Importance --- */}
                                        {firePrediction.feature_importances && (
                                            <div style={{
                                                marginTop: '10px',
                                                padding: '15px',
                                                backgroundColor: '#f9f9f9',
                                                borderRadius: '4px',
                                                border: '1px solid #eee',
                                                fontSize: '13px',
                                                lineHeight: '1.6'
                                            }}>
                                                <strong style={{ display: 'block', marginBottom: '8px' }}>Legend:</strong>
                                                <ul style={{ paddingLeft: '20px', margin: 0 }}>
                                                    <li><strong>T2M:</strong> Temperature at 2 meters above ground</li>
                                                    <li><strong>PRECTOTCORR:</strong> Precipitation</li>
                                                    <li><strong>RH2M:</strong> Relative Humidity at 2 meters above ground</li>
                                                    <li><strong>WS2M:</strong> Wind Speed at 2 meters above ground</li>
                                                    <li><strong>NDVI:</strong> Normalized Difference Vegetation Index</li>
                                                    <li><strong>Lag 1:</strong> The value of the feature from the previous month.</li>
                                                    <li><strong>Lag 2:</strong> The value of the feature from two months ago.</li>
                                                </ul>
                                            </div>
                                        )}

                                        <ul></ul>
                                        
                                        {/* --- Original Table of Results --- */}
                                        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                            <thead>
                                                <tr>
                                                    <th style={{ border: '1px solid #ddd', padding: '8px' }}>Month</th>
                                                    <th style={{ border: '1px solid #ddd', padding: '8px' }}>Predicted Fire Count</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {firePrediction.forecast.map((item, index) => (
                                                    <tr key={index}>
                                                        <td style={{ border: '1px solid #ddd', padding: '8px' }}>{new Date(item.month).toLocaleString('default', { month: 'long', year: 'numeric' })}</td>
                                                        <td style={{ border: '1px solid #ddd', padding: '8px' }}>{Math.round(item.predicted_fire_count)}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>

                                        {/* --- Model Information Note --- */}
                                        <div style={{
                                            marginTop: '20px',
                                            padding: '12px',
                                            fontSize: '13px',
                                            color: '#444',
                                            backgroundColor: '#f8f9fa',
                                            border: '1px solid #e9ecef',
                                            borderRadius: '4px',
                                            lineHeight: '1.6'
                                        }}>
                                            <strong>Note:</strong> The prediction model is trained using Temperature (T2M), Precipitation (PRECTOTCORR), Relative Humidity (RH2M), Wind Speed (WS2M), and Normalized Difference Vegetation Index (NDVI), along with their 1 and 2-month lagged values as predictors.
                                            <br />
                                            Weather data is sourced from <a href="https://power.larc.nasa.gov/" target="_blank" rel="noopener noreferrer">NASA POWER</a>, and NDVI data is from <a href="https://developers.google.com/earth-engine/datasets/catalog/MODIS_061_MOD13A3" target="_blank" rel="noopener noreferrer">Google Earth Engine MOD13A3</a>.
                                        </div>
                                    </div>
                                )}

                                {/* Prophet model - original simple display */}
                                {firePrediction && selectedFireModel === 'prophet' && (
                                    <div style={{ marginTop: '20px' }}>
                                        <h4>Forecast Results ({firePrediction.model_type})</h4>
                                        <p>Total predicted fire alerts over {firePrediction.timestep} months: <strong>{Math.round(firePrediction.total)}</strong></p>

                                        {/* --- CHART: Forecast Trend --- */}
                                        <div style={{ height: '300px', marginBottom: '40px' }}>
                                            <Line
                                                options={{
                                                    responsive: true,
                                                    maintainAspectRatio: false,
                                                    plugins: {
                                                        legend: { position: 'top' },
                                                        title: { display: true, text: 'Fire Alert Forecast Trend', font: { size: 16 } }
                                                    },
                                                    scales: {
                                                        y: {
                                                            beginAtZero: true,
                                                            title: { display: true, text: 'Predicted Fire Alerts' }
                                                        }
                                                    }
                                                }}
                                                data={{
                                                    labels: firePrediction.forecast.map(d => new Date(d.month).toLocaleString('default', { month: 'short', year: 'numeric' })),
                                                    datasets: [{
                                                        label: 'Predicted Fire Alerts',
                                                        data: firePrediction.forecast.map(d => d.predicted_fire_count),
                                                        borderColor: 'rgb(255, 99, 132)',
                                                        backgroundColor: 'rgba(255, 99, 132, 0.5)',
                                                        fill: true,
                                                    }]
                                                }}
                                            />
                                        </div>

                                        {/* --- Table of Results --- */}
                                        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                            <thead>
                                                <tr>
                                                    <th style={{ border: '1px solid #ddd', padding: '8px' }}>Month</th>
                                                    <th style={{ border: '1px solid #ddd', padding: '8px' }}>Predicted Fire Count</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {firePrediction.forecast.map((item, index) => (
                                                    <tr key={index}>
                                                        <td style={{ border: '1px solid #ddd', padding: '8px' }}>{new Date(item.month).toLocaleString('default', { month: 'long', year: 'numeric' })}</td>
                                                        <td style={{ border: '1px solid #ddd', padding: '8px' }}>{Math.round(item.predicted_fire_count)}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </div>
            );

            // Add default case
            default:
                return <div>Please select a category from the sidebar.</div>;
        }
    };

    const tifFiles = {
        "2020": {
            mangrove1: "tif/mangrove/2020/Asajaya.tif",
            mangrove2: "tif/mangrove/2020/Betong.tif",
            mangrove3: "tif/mangrove/2020/Dalat.tif",
            mangrove4: "tif/mangrove/2020/Daro.tif",
            mangrove5: "tif/mangrove/2020/Kuching.tif",
            mangrove6: "tif/mangrove/2020/Lawas.tif",
            mangrove7: "tif/mangrove/2020/Limbang.tif",
            mangrove8: "tif/mangrove/2020/Lundu.tif",
            mangrove9: "tif/mangrove/2020/Matu.tif",
            mangrove12: "tif/mangrove/2020/Mukah.tif",
            mangrove13: "tif/mangrove/2020/Samarahan.tif",
            mangrove14: "tif/mangrove/2020/Saratok.tif",
            mangrove15: "tif/mangrove/2020/Sarikei.tif",
            mangrove16: "tif/mangrove/2020/Simunjan.tif",
            forest1: "tif/forest/2020/Asajaya.tif",
            forest2: "tif/forest/2020/Bau.tif",
            forest3: "tif/forest/2020/Belaga.tif",
            forest4: "tif/forest/2020/Betong.tif",
            forest5: "tif/forest/2020/Bintulu.tif",
            forest6: "tif/forest/2020/Dalat.tif",
            forest7: "tif/forest/2020/Daro.tif",
            forest8: "tif/forest/2020/Julau.tif",
            forest9: "tif/forest/2020/Kanowit.tif",
            forest10: "tif/forest/2020/Kapit.tif",
            forest11: "tif/forest/2020/Kuching.tif",
            forest12: "tif/forest/2020/Lawas.tif",
            forest13: "tif/forest/2020/Limbang.tif",
            forest14: "tif/forest/2020/Lubok_Antu.tif",
            forest15: "tif/forest/2020/Lundu.tif",
            forest16: "tif/forest/2020/Marudi.tif",
            forest17: "tif/forest/2020/Matu.tif",
            forest18: "tif/forest/2020/Meradong.tif",
            forest19: "tif/forest/2020/Miri.tif",
            forest20: "tif/forest/2020/Mukah.tif",
            forest21: "tif/forest/2020/Pakan.tif",
            forest22: "tif/forest/2020/Samarahan.tif",
            forest23: "tif/forest/2020/Saratok.tif",
            forest24: "tif/forest/2020/Sarikei.tif",
            forest25: "tif/forest/2020/Selangau.tif",
            forest26: "tif/forest/2020/Serian.tif",
            forest27: "tif/forest/2020/Sibu.tif",
            forest28: "tif/forest/2020/Simunjan.tif",
            forest29: "tif/forest/2020/Song.tif",
            forest30: "tif/forest/2020/Sri_Aman.tif",
            forest31: "tif/forest/2020/Tatau.tif",
        },
        "2015": {
            mangrove1: "tif/mangrove/2015/Asajaya.tif",
            mangrove2: "tif/mangrove/2015/Betong.tif",
            mangrove3: "tif/mangrove/2015/Dalat.tif",
            mangrove4: "tif/mangrove/2015/Daro.tif",
            mangrove5: "tif/mangrove/2015/Kuching.tif",
            mangrove6: "tif/mangrove/2015/Lawas.tif",
            mangrove7: "tif/mangrove/2015/Limbang.tif",
            mangrove8: "tif/mangrove/2015/Lundu.tif",
            mangrove9: "tif/mangrove/2015/Matu.tif",
            mangrove10: "tif/mangrove/2015/Meradong.tif",
            mangrove11: "tif/mangrove/2015/Miri.tif",
            mangrove12: "tif/mangrove/2015/Mukah.tif",
            mangrove13: "tif/mangrove/2015/Samarahan.tif",
            mangrove14: "tif/mangrove/2015/Saratok.tif",
            mangrove15: "tif/mangrove/2015/Sarikei.tif",
            mangrove16: "tif/mangrove/2015/Simunjan.tif",
            forest1: "tif/forest/2015/Asajaya.tif",
            forest2: "tif/forest/2015/Bau.tif",
            forest3: "tif/forest/2015/Belaga.tif",
            forest4: "tif/forest/2015/Betong.tif",
            forest5: "tif/forest/2015/Bintulu.tif",
            forest6: "tif/forest/2015/Dalat.tif",
            forest7: "tif/forest/2015/Daro.tif",
            forest8: "tif/forest/2015/Julau.tif",
            forest9: "tif/forest/2015/Kanowit.tif",
            forest10: "tif/forest/2015/Kapit.tif",
            forest11: "tif/forest/2015/Kuching.tif",
            forest12: "tif/forest/2015/Lawas.tif",
            forest13: "tif/forest/2015/Limbang.tif",
            forest14: "tif/forest/2015/Lubok_Antu.tif",
            forest15: "tif/forest/2015/Lundu.tif",
            forest16: "tif/forest/2015/Marudi.tif",
            forest17: "tif/forest/2015/Matu.tif",
            forest18: "tif/forest/2015/Meradong.tif",
            forest19: "tif/forest/2015/Miri.tif",
            forest20: "tif/forest/2015/Mukah.tif",
            forest21: "tif/forest/2015/Pakan.tif",
            forest22: "tif/forest/2015/Samarahan.tif",
            forest23: "tif/forest/2015/Saratok.tif",
            forest24: "tif/forest/2015/Sarikei.tif",
            forest25: "tif/forest/2015/Selangau.tif",
            forest26: "tif/forest/2015/Serian.tif",
            forest27: "tif/forest/2015/Sibu.tif",
            forest28: "tif/forest/2015/Simunjan.tif",
            forest29: "tif/forest/2015/Song.tif",
            forest30: "tif/forest/2015/Sri_Aman.tif",
            forest31: "tif/forest/2015/Tatau.tif",
            cropland1: "tif/cropland/2015/cropland_1.tif",
            cropland2: "tif/cropland/2015/cropland_2.tif",
            cropland3: "tif/cropland/2015/cropland_3.tif",
            cropland4: "tif/cropland/2015/cropland_4.tif",
            cropland5: "tif/cropland/2015/cropland_5.tif",
            cropland6: "tif/cropland/2015/cropland_6.tif",
            cropland7: "tif/cropland/2015/cropland_7.tif",
            cropland8: "tif/cropland/2015/cropland_8.tif",
            cropland9: "tif/cropland/2015/cropland_9.tif",
            cropland10: "tif/cropland/2015/cropland_10.tif",
            cropland11: "tif/cropland/2015/cropland_11.tif",
            cropland12: "tif/cropland/2015/cropland_12.tif",
            cropland13: "tif/cropland/2015/cropland_13.tif",
            cropland14: "tif/cropland/2015/cropland_14.tif",
            cropland15: "tif/cropland/2015/cropland_15.tif",
            cropland16: "tif/cropland/2015/cropland_16.tif",
            cropland17: "tif/cropland/2015/cropland_17.tif",
            cropland18: "tif/cropland/2015/cropland_18.tif",
            cropland19: "tif/cropland/2015/cropland_19.tif",
            cropland20: "tif/cropland/2015/cropland_20.tif",
            cropland21: "tif/cropland/2015/cropland_21.tif",
            cropland22: "tif/cropland/2015/cropland_22.tif",
            cropland23: "tif/cropland/2015/cropland_23.tif",
            cropland24: "tif/cropland/2015/cropland_24.tif",
            cropland25: "tif/cropland/2015/cropland_25.tif",
            cropland26: "tif/cropland/2015/cropland_26.tif",
            cropland27: "tif/cropland/2015/cropland_27.tif",
            cropland28: "tif/cropland/2015/cropland_28.tif",
            cropland29: "tif/cropland/2015/cropland_29.tif",
            cropland30: "tif/cropland/2015/cropland_30.tif",
            cropland31: "tif/cropland/2015/cropland_31.tif",
        },
        "2010": {
            mangrove1: "tif/mangrove/2010/Asajaya.tif",
            mangrove2: "tif/mangrove/2010/Betong.tif",
            mangrove3: "tif/mangrove/2010/Dalat.tif",
            mangrove4: "tif/mangrove/2010/Daro.tif",
            mangrove5: "tif/mangrove/2010/Kuching.tif",
            mangrove6: "tif/mangrove/2010/Lawas.tif",
            mangrove7: "tif/mangrove/2010/Limbang.tif",
            mangrove8: "tif/mangrove/2010/Lundu.tif",
            mangrove9: "tif/mangrove/2010/Matu.tif",
            mangrove10: "tif/mangrove/2010/Meradong.tif",
            mangrove11: "tif/mangrove/2010/Miri.tif",
            mangrove12: "tif/mangrove/2010/Mukah.tif",
            mangrove13: "tif/mangrove/2010/Samarahan.tif",
            mangrove14: "tif/mangrove/2010/Saratok.tif",
            mangrove15: "tif/mangrove/2010/Sarikei.tif",
            mangrove16: "tif/mangrove/2010/Simunjan.tif",
            forest1: "tif/forest/2010/Asajaya.tif",
            forest2: "tif/forest/2010/Bau.tif",
            forest3: "tif/forest/2010/Belaga.tif",
            forest4: "tif/forest/2010/Betong.tif",
            forest5: "tif/forest/2010/Bintulu.tif",
            forest6: "tif/forest/2010/Dalat.tif",
            forest7: "tif/forest/2010/Daro.tif",
            forest8: "tif/forest/2010/Julau.tif",
            forest9: "tif/forest/2010/Kanowit.tif",
            forest10: "tif/forest/2010/Kapit.tif",
            forest11: "tif/forest/2010/Kuching.tif",
            forest12: "tif/forest/2010/Lawas.tif",
            forest13: "tif/forest/2010/Limbang.tif",
            forest14: "tif/forest/2010/Lubok_Antu.tif",
            forest15: "tif/forest/2010/Lundu.tif",
            forest16: "tif/forest/2010/Marudi.tif",
            forest17: "tif/forest/2010/Matu.tif",
            forest18: "tif/forest/2010/Meradong.tif",
            forest19: "tif/forest/2010/Miri.tif",
            forest20: "tif/forest/2010/Mukah.tif",
            forest21: "tif/forest/2010/Pakan.tif",
            forest22: "tif/forest/2010/Samarahan.tif",
            forest23: "tif/forest/2010/Saratok.tif",
            forest24: "tif/forest/2010/Sarikei.tif",
            forest25: "tif/forest/2010/Selangau.tif",
            forest26: "tif/forest/2010/Serian.tif",
            forest27: "tif/forest/2010/Sibu.tif",
            forest28: "tif/forest/2010/Simunjan.tif",
            forest29: "tif/forest/2010/Song.tif",
            forest30: "tif/forest/2010/Sri_Aman.tif",
            forest31: "tif/forest/2010/Tatau.tif",
        },
        "2023": {}
    };

    const testItems = [
        { label: "High", color: "#007f3f" },
        { label: "Medium", color: "#ffff00" },
        { label: "Low", color: "#ff0000" }
    ];

    // Sidebar content as a separate component for reuse
    const SidebarContent = () => (
        <div style={{ 
            width: isMobile ? '90px' : isTablet ? '100px' : '120px', 
            backgroundColor: '#e8e6d9', 
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            padding: '1vh 0',
            borderRight: '1px solid #ccc'
        }}>
            <div style={{ display: 'flex', flexDirection: 'column', height: '100%', justifyContent: 'space-evenly' }}>
                {navigationItems.map(item => (
                    <SidebarNavItem
                        key={item.category}
                        category={item.category}
                        label={item.label}
                        icon={item.icon}
                        bgColor={item.bgColor}
                        textColor={item.textColor}
                        activeCategory={activeCategory}
                        setActiveCategory={(category) => {
                            setActiveCategory(category);
                            if (isMobile) {
                                setSidebarDrawerOpen(false);
                                setContentDrawerOpen(true);
                            }
                        }}
                        isMobile={isMobile}
                    />
                ))}
            </div>
        </div>
    );

    return (
        <div style={{ 
            height: isMobile ? "auto" : "100vh",
            width: "100vw", 
            overflow: isMobile ? 'auto' : 'hidden',
            display: 'flex',
            flexDirection: isMobile ? 'column' : 'row',
            position: 'relative'
        }}>
            <style>{activeLayerStyles}</style>
            
            {/* Desktop sidebar */}
            {!isMobile && <SidebarContent />}
            
            {/* Mobile sidebar drawer */}
            {isMobile && (
                <Drawer
                    anchor="left"
                    open={sidebarDrawerOpen}
                    onClose={() => setSidebarDrawerOpen(false)}
                >
                    <Box sx={{ 
                        pt: 2,
                        display: 'flex',
                        flexDirection: 'column',
                        height: '100%'
                    }}>
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', px: 1, mb: 2 }}>
                            <IconButton onClick={() => setSidebarDrawerOpen(false)}>
                                <ChevronLeftIcon />
                            </IconButton>
                        </Box>
                        <SidebarContent />
                    </Box>
                </Drawer>
            )}

            {/* Desktop content panel */}
            {!isMobile && activeCategory && (
                <div style={{ 
                    width: isTablet ? '250px' : '340px', 
                    backgroundColor: '#f5f5f0', 
                    height: '100%',
                    padding: '15px',
                    overflow: 'auto',
                    zIndex: 1000,
                    position: 'relative'
                }}>
                    <IconButton
                        onClick={() => setActiveCategory(null)}
                        style={{ position: 'absolute', top: 8, right: 8 }}
                        aria-label="Close panel"
                    >
                        <CloseIcon />
                    </IconButton>
                    <div style={{ paddingBottom: '50px' }}> 
                        {renderContent()}
                    </div>
                </div>
            )}
            
            {/* Mobile content drawer */}
            {isMobile && (
                <Drawer
                    anchor="bottom"
                    open={contentDrawerOpen}
                    onClose={() => setContentDrawerOpen(false)}
                >
                    <Box sx={{ 
                        maxHeight: '60vh',
                        overflow: 'auto',
                        p: 2,
                        pt: 1,
                        pb: 3
                    }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                            <h3 style={{ margin: 0 }}>
                                {navigationItems.find(item => item.category === activeCategory)?.label}
                            </h3>
                            <IconButton onClick={() => setContentDrawerOpen(false)}>
                                <CloseIcon />
                            </IconButton>
                        </Box>
                        {renderContent()}
                    </Box>
                </Drawer>
            )}

            {/* Map container - no bottom margin */}
            <div id="map" style={{ 
                height: isMobile ? "80vh" : "100%",
                width: "100%", 
                flex: isMobile ? 'none' : 1,
                marginBottom: 0, // Remove margin
                position: 'relative' // Allows absolute positioning inside
            }}>        
                <MapContainer 
                    center={[1.5, 110.3]} 
                    zoom={isMobile ? 6 : 11} 
                    style={{ height: "100%", width: "100%" }}
                    zoomControl={!isMobile}
                >
                    <LayersControl position="topright">
                        <BaseLayer checked name="OpenStreetMap">
                            <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
                        </BaseLayer>
                    </LayersControl>

                    {/* Map Controller for locking/unlocking */}
                    <MapController 
                        comparisonMode={comparisonMode}
                        district={comparisonConfig.district}
                        mangroveToFileMap={mangroveToFileMap}
                        croplandToFileMap={croplandToFileMap}
                        layerType={comparisonConfig.layerType}
                    />

                    <ZoomToDistrict 
                        district={comparisonConfig.district}
                        layerType={comparisonConfig.layerType}
                        shouldZoom={comparisonMode}
                    />

                    {/* Comparison Mode Layers */}
                    {comparisonMode && comparisonConfig.district && (() => {
                        const { layerType, district, leftYear, rightYear, sliderPosition } = comparisonConfig;
                        
                        if (layerType === 'mangrove') {
                            const leftKey = mangroveToFileMap[district];
                            const rightKey = mangroveToFileMap[district];
                            const leftUrl = tifFiles[leftYear]?.[leftKey];
                            const rightUrl = tifFiles[rightYear]?.[rightKey];

                            console.log('Rendering mangrove comparison:', { 
                                leftUrl, 
                                rightUrl, 
                                leftYear, 
                                rightYear,
                                leftKey,
                                rightKey,
                                sliderPosition 
                            });

                            if (leftUrl && rightUrl) {
                                const rightClip = sliderPosition;
                                const leftClip = 100 - sliderPosition;

                                return (
                                    <>
                                        <GeoTIFFLayer
                                            key={`compare-left-${district}-${leftYear}`}
                                            url={leftUrl}
                                            opacity={1.0}
                                            layerType="mangrove"
                                            district={district}
                                            year={leftYear}
                                            clipPercent={leftClip}
                                            clipDirection="left"
                                            onBoundsLoad={handleBoundsLoad}
                                        />
                                        
                                        <GeoTIFFLayer
                                            key={`compare-right-${district}-${rightYear}`}
                                            url={rightUrl}
                                            opacity={1.0}
                                            layerType="mangrove"
                                            district={district}
                                            year={rightYear}
                                            clipPercent={rightClip}
                                            clipDirection="right"
                                            onBoundsLoad={handleBoundsLoad}
                                        />
                                    </>
                                );
                            } else {
                                console.error('Missing URLs:', { leftUrl, rightUrl });
                                return null;
                            }
                        } else if (layerType === 'forest') {
                            const leftKey = districtToFileMap[district];
                            const rightKey = districtToFileMap[district];
                            const leftUrl = tifFiles[leftYear]?.[leftKey];
                            const rightUrl = tifFiles[rightYear]?.[rightKey];

                            if (leftUrl && rightUrl) {
                                const rightClip = sliderPosition;
                                const leftClip = 100 - sliderPosition;

                                return (
                                    <>
                                        <GeoTIFFLayer
                                            key={`compare-left-${district}-${leftYear}`}
                                            url={leftUrl}
                                            opacity={1.0}
                                            layerType="forest"
                                            district={district}
                                            year={leftYear}
                                            clipPercent={leftClip}
                                            clipDirection="left"
                                            onBoundsLoad={handleBoundsLoad}
                                        />
                                        
                                        <GeoTIFFLayer
                                            key={`compare-right-${district}-${rightYear}`}
                                            url={rightUrl}
                                            opacity={1.0}
                                            layerType="forest"
                                            district={district}
                                            year={rightYear}
                                            clipPercent={rightClip}
                                            clipDirection="right"
                                            onBoundsLoad={handleBoundsLoad}
                                        />
                                    </>
                                );
                            } else {
                                console.error('Missing URLs:', { leftUrl, rightUrl });
                                return null;
                            }
                        } else if (layerType === 'cropland') {
                            // Handle cropland comparison
                            if (leftYear === '2015' && rightYear === '2019') {
                                const leftKey = croplandToFileMap[district];
                                const leftUrl = tifFiles['2015']?.[leftKey];
                                const rightGeoJsonUrl = `/vector/cropland/2019/oilpalm_${district.replace(/ /g, '')}.geojson`;

                                if (leftUrl) {
                                    const rightClip = sliderPosition;
                                    const leftClip = 100 - sliderPosition;
                                    
                                    // Calculate opacity for the GeoJSON layer
                                    // When slider is at 0% (far left), 2019 should be fully visible (opacity 1)
                                    // When slider is at 100% (far right), 2019 should be invisible (opacity 0)
                                    const geoJsonOpacity = (100 - sliderPosition) / 100;

                                    return (
                                        <>
                                            {/* Left side - 2015 TIFF with clipping */}
                                            <GeoTIFFLayer
                                                key={`compare-left-${district}-2015`}
                                                url={leftUrl}
                                                opacity={1.0}
                                                layerType="cropland"
                                                district={district}
                                                year="2015"
                                                clipPercent={leftClip}
                                                clipDirection="left"
                                                onBoundsLoad={handleBoundsLoad}
                                            />
                                            
                                            {/* Right side - 2019 GeoJSON with opacity transition */}
                                            <GeoJsonWithUrl 
                                                key={`compare-right-${district}-2019`}
                                                url={rightGeoJsonUrl}
                                                style={{ 
                                                    color: '#f0e68c', 
                                                    weight: 2, 
                                                    fillOpacity: 0.7 * geoJsonOpacity,  // Apply opacity
                                                    fillColor: '#f0e68c',
                                                    opacity: geoJsonOpacity  // Border opacity
                                                }}
                                                onEachFeature={(feature, layer) => {
                                                    if (feature.properties) {
                                                        const area = feature.properties.area 
                                                            ? (feature.properties.area / 10000).toFixed(2) 
                                                            : feature.properties.Shape_Area 
                                                                ? (feature.properties.Shape_Area / 10000).toFixed(2)
                                                                : "N/A";
                                                        const name = feature.properties.name || feature.properties.Name || feature.properties.LICENSEE || "Oil Palm Concession";
                                                        layer.bindPopup(`
                                                            <strong>${name}</strong><br>
                                                            <b>Type:</b> Oil Palm Concession<br>
                                                            <b>Year:</b> 2019<br>
                                                            <b>District:</b> ${district}
                                                        `);
                                                    }
                                                }}
                                                onBoundsLoad={handleBoundsLoad}
                                            />
                                        </>
                                    );
                                }
                            } else if (leftYear === '2019' && rightYear === '2015') {
                                const leftUrl = `/vector/cropland/2019/oilpalm_${district.replace(/ /g, '')}.geojson`;
                                const rightKey = croplandToFileMap[district];
                                const rightUrl = tifFiles['2015']?.[rightKey];
                                
                                if (!rightUrl) {
                                    console.error('Missing cropland 2015 URL for district:', district);
                                    return null;
                                }

                                const rightClip = sliderPosition;
                                const leftClip = 100 - sliderPosition;
                                
                                // Calculate opacity for the GeoJSON layer (on the left side this time)
                                // When slider is at 0% (far left), 2019 should be invisible
                                // When slider is at 100% (far right), 2019 should be fully visible
                                const geoJsonOpacity = sliderPosition / 100;

                                return (
                                    <>
                                        {/* Left side - 2019 GeoJSON with opacity transition */}
                                        <GeoJsonWithUrl 
                                            key={`compare-left-${district}-2019`}
                                            url={leftUrl}
                                            style={{ 
                                                color: '#f0e68c', 
                                                weight: 2, 
                                                fillOpacity: 0.7 * geoJsonOpacity,  // Apply opacity
                                                fillColor: '#f0e68c',
                                                opacity: geoJsonOpacity  // Border opacity
                                            }}
                                            onEachFeature={(feature, layer) => {
                                                if (feature.properties) {
                                                    const area = feature.properties.area 
                                                        ? (feature.properties.area / 10000).toFixed(2) 
                                                        : feature.properties.Shape_Area 
                                                            ? (feature.properties.Shape_Area / 10000).toFixed(2)
                                                            : "N/A";
                                                    const name = feature.properties.name || feature.properties.Name || feature.properties.LICENSEE || "Oil Palm Concession";
                                                    layer.bindPopup(`
                                                        <strong>${name}</strong><br>
                                                        <b>Type:</b> Oil Palm Concession<br>
                                                        <b>Year:</b> 2019<br>
                                                        <b>District:</b> ${district}
                                                    `);
                                                }
                                            }}
                                        />
                                        
                                        {/* Right side - 2015 TIFF with clipping */}
                                        <GeoTIFFLayer
                                            key={`compare-right-${district}-2015`}
                                            url={rightUrl}
                                            opacity={1.0}
                                            layerType="cropland"
                                            district={district}
                                            year="2015"
                                            clipPercent={rightClip}
                                            clipDirection="right"
                                        />
                                    </>
                                );
                            }
                        }
                        return null;
                    })()}
                    
                    {/* Map Layers rendered in user-defined order */}
                    {!comparisonMode && layerOrder.slice().reverse().map((layer) => {
                        if (layer.type === 'mangrove') {
                            return (
                                <Pane key={`mangrove-pane-${layer.year}`} name="mangrove-pane" style={{ zIndex: 650 }}>
                                    {layer.districts.map(district => {
                                        const key = mangroveToFileMap[district];
                                        const url = tifFiles[selectedMangroveYear] && tifFiles[selectedMangroveYear][key];
                                        return url && mangroveLayersVisible[district] && visibleLayers[key]
                                            ? <GeoTIFFLayer key={key} url={url} opacity={1.0} layerType="mangrove" district={district} year={selectedMangroveYear} onRegister={registerMangroveLayer} onUnregister={unregisterMangroveLayer} pane="mangrove-pane" />
                                            : null;
                                    })}
                                </Pane>
                            );
                        }
                        
                        if (layer.type === 'forest') {
                            return layer.districts.map(district => {
                                const key = districtToFileMap[district];
                                const url = tifFiles[selectedForestYear] && tifFiles[selectedForestYear][key]; // Use selectedForestYear
                                return url && forestLayersVisible[district] && visibleLayers[key] ? 
                                    <GeoTIFFLayer key={key} url={url} opacity={1.0} onClick={(latlng, value) => handleLayerClick(latlng, value, 'forest', district)} /> : null;
                            });
                        }
                        
                        if (layer.type === 'cropland') {
                            if (selectedCroplandYear === "2019") {
                                // Render district-wise GeoJSON layers for 2019
                                return layer.districts.map(district => {
                                    const isVisible = cropland2019LayersVisible[district];
                                    if (!isVisible) return null;

                                    const url = `/vector/cropland/2019/oilpalm_${district.replace(/ /g, '')}.geojson`;
                                    const layerKey = `cropland-2019-${district}`;

                                    return (
                                        <GeoJsonWithUrl 
                                            key={layerKey}
                                            url={url}
                                            style={() => ({
                                                fillColor: '#f0e68c',
                                                weight: 1,
                                                opacity: 1,
                                                color: '#dac653',
                                                fillOpacity: 0.7
                                            })}
                                            onEachFeature={(feature, layer) => {
                                                if (feature.properties) {
                                                    const area = feature.properties.area 
                                                        ? (feature.properties.area / 10000).toFixed(2) 
                                                        : feature.properties.Shape_Area 
                                                            ? (feature.properties.Shape_Area / 10000).toFixed(2)
                                                            : "N/A";
                                                    const name = feature.properties.name || feature.properties.Name || feature.properties.LICENSEE || "Oil Palm Concession";
                                                    layer.bindPopup(`
                                                        <strong>${name}</strong><br>
                                                        <b>Type:</b> Oil Palm Concession<br>
                                                        <b>Year:</b> 2019<br>
                                                        <b>District:</b> ${district}
                                                    `);
                                                }
                                            }}
                                        />
                                    );
                                });
                            } else {
                                // Render district-wise TIFF layers for 2015
                                return layer.districts.map(district => {
                                    const key = croplandToFileMap[district];
                                    const url = tifFiles[selectedCroplandYear] && tifFiles[selectedCroplandYear][key];
                                    return url && croplandLayersVisible[district] && visibleLayers[key] ? 
                                        <GeoTIFFLayer key={key} url={url} opacity={1.0} /> : null;
                                });
                            }
                        }
                        
                        if (layer.type === 'peatland') {
                            return layer.districts.map(district => {
                                const key = peatlandToFileMap[district];
                                const districtFileName = 'peatland' + district.replace(/ /g, '') + '.geojson';
                                const geoJsonUrl = `/vector/peatland/sarawak/${districtFileName}`;
                                
                                return peatlandLayersVisible[district] && visibleLayers[key] ? (
                                    <GeoJsonWithUrl 
                                        key={`peatland-${district}-2019`}
                                        url={geoJsonUrl}
                                        style={() => ({
                                            fillColor: '#8B4513',
                                            weight: 1,
                                            opacity: 1,
                                            color: '#6b330e',
                                            fillOpacity: 0.6
                                        })}
                                        onEachFeature={(feature, layer) => {
                                            if (feature.properties) {
                                                const area = feature.properties.shape_Area 
                                                    ? (feature.properties.shape_Area / 10000).toFixed(2) 
                                                    : "N/A";
                                                layer.bindPopup(`
                                                    <strong>${district}</strong><br>
                                                    <b>Peatland Area:</b> ${area} hectares<br>
                                                    <b>Year:</b> 2019
                                                `);
                                            }
                                        }}
                                    />
                                ) : null;
                            });
                        }
                        
                        return null;
                    })}

                    {/* Comparison Slider */}
                    {comparisonMode && (
                        <ComparisonSlider
                            position={comparisonConfig.sliderPosition}
                            onPositionChange={(pos) => setComparisonConfig({
                                ...comparisonConfig,
                                sliderPosition: pos
                            })}
                            leftYear={comparisonConfig.leftYear}
                            rightYear={comparisonConfig.rightYear}
                            layerType={comparisonConfig.layerType}
                        />
                    )}

                    {/* Map-level click handler for mangrove */}
                    <MangroveClickHandler />

                    {/* Legends container with mobile-friendly positioning */}
                    <div style={{
                        position: 'absolute',
                        top: isMobile ? '60px' : '10px', // Adjusted top for mobile
                        right: '10px',
                        zIndex: 1000,
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '10px',
                        alignItems: 'flex-end',
                        maxWidth: isMobile ? '160px' : '300px'  // Narrower legend on mobile
                    }}>
                        {/* Legend toggle button for mobile */}
                        {isMobile && (
                            <div style={{
                                backgroundColor: 'white',
                                borderRadius: '4px',
                                padding: '5px',
                                boxShadow: '0 0 10px rgba(0,0,0,0.15)',
                                cursor: 'pointer',
                                marginBottom: '5px',
                                alignSelf: 'flex-end'
                            }} onClick={() => setLegendsVisible(!legendsVisible)}>
                                <span role="img" aria-label="Legend">🗺️ {legendsVisible ? 'Hide' : 'Show'} Legend</span>
                            </div>
                        )}

                        {/* Only show legends if on desktop or if legendsVisible is true on mobile */}
                        {(!isMobile || legendsVisible) && (
                            <>
                                {/* Forest Legend - Always on top because it's the biggest */}
                                {((Object.entries(tifFiles[selectedForestYear]).filter(([key]) => key.startsWith("forest")).some(([key]) => visibleLayers[key])) ||
                                    (comparisonMode && comparisonConfig.layerType === 'forest')) && (
                                    <GroupedVerticalLegends
                                        title="Forest Coverage" 
                                        groupedItems={forestLegendItems} 
                                        stats={
                                            comparisonMode && comparisonConfig.layerType === 'forest'
                                                ? `Comparing: ${comparisonConfig.district}`
                                                : `Year: ${selectedForestYear} - Showing ${Object.keys(forestLayersVisible).filter(d => forestLayersVisible[d]).length} districts`
                                        }
                                        isMobile={isMobile} // Pass isMobile prop
                                    />
                                )}
                                
                                {/* Mangrove Legend */}
                                {((Object.entries(tifFiles[selectedMangroveYear]).filter(([key]) => key.startsWith("mangrove")).some(([key]) => visibleLayers[key])) ||
                                    (comparisonMode && comparisonConfig.layerType === 'mangrove')) && (
                                    <div style={{
                                        backgroundColor: 'white',
                                        borderRadius: '6px',
                                        boxShadow: '0 0 10px rgba(0,0,0,0.15)'
                                    }}>
                                        <MapLegend
                                            title="Mangrove Coverage"
                                            items={[{ color: '#008000', label: 'Mangrove Area' }]}
                                            stats={
                                                comparisonMode && comparisonConfig.layerType === 'mangrove'
                                                    ? `Comparing: ${comparisonConfig.district}`
                                                    : `Year: ${selectedMangroveYear} - Showing ${Object.keys(mangroveLayersVisible).filter(d => mangroveLayersVisible[d]).length} districts`
                                            }
                                            position="custom"
                                            isMobile={isMobile} // Pass isMobile prop
                                        />
                                    </div>
                                )}
                                
                                {/* Cropland Legend */}
                                {((selectedCroplandYear === "2015" && Object.entries(tifFiles[selectedCroplandYear]).filter(([key]) => key.startsWith("cropland")).some(([key]) => visibleLayers[key])) ||
                                    (selectedCroplandYear === "2019" && Object.values(cropland2019LayersVisible).some(v => v)) ||
                                    (comparisonMode && comparisonConfig.layerType === 'cropland')) && (
                                    <div style={{
                                        backgroundColor: 'white',
                                        borderRadius: '6px',
                                        boxShadow: '0 0 10px rgba(0,0,0,0.15)'
                                    }}>
                                        <MapLegend
                                            title="Cropland Extent"
                                            items={[{ color: '#f0e68c', label: (comparisonMode && comparisonConfig.layerType === 'cropland') || selectedCroplandYear === "2019" ? 'Oil Palm Concessions' : 'Cropland Area' }]}
                                            stats={
                                                comparisonMode && comparisonConfig.layerType === 'cropland'
                                                    ? `Comparing: ${comparisonConfig.district}`
                                                    : selectedCroplandYear === "2019"
                                                        ? `Year: ${selectedCroplandYear} - Showing ${Object.keys(cropland2019LayersVisible).filter(d => cropland2019LayersVisible[d]).length} districts`
                                                        : `Year: ${selectedCroplandYear} - Showing ${Object.keys(croplandLayersVisible).filter(d => croplandLayersVisible[d]).length} districts`
                                            }
                                            position="custom"
                                            isMobile={isMobile}
                                        />
                                    </div>
                                )}
                                
                                {/* Peatland Legend */}
                                {Object.values(peatlandLayersVisible).some(v => v) && (
                                    <div style={{ 
                                        backgroundColor: 'white',
                                        borderRadius: '6px',
                                        boxShadow: '0 0 10px rgba(0,0,0,0.15)'
                                    }}>
                                        <MapLegend 
                                            title="Peatland Distribution" 
                                            items={[{ color: '#8B4513', label: 'Peatland Area' }]} 
                                            stats={`Year: 2019 - Showing ${Object.keys(peatlandLayersVisible).filter(d => peatlandLayersVisible[d]).length} districts`}
                                            position="custom"
                                            isMobile={isMobile} // Pass isMobile prop
                                        />
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                    
                    {/* Fire Markers */}
                    {filteredData.map((alert, index) => (
                        <Marker
                            key={index}
                            position={[parseFloat(alert.latitude), parseFloat(alert.longitude)]}
                            icon={L.divIcon({
                                className: "custom-icon",
                                html: `<div style="background-color: ${getMarkerColor(
                                    alert.confidence
                                )}; width: 12px; height: 12px; border-radius: 50%;"></div>`,
                            })}
                            eventHandlers={{
                                click: () => setSelectedAlert(alert),
                            }}
                        >
                            <Popup>
                                <strong>Fire Alert</strong>
                                <br />
                                <b>Confidence:</b> {alert.confidence.toUpperCase()}
                                <br />
                                <b>Date:</b> {alert.acq_date}
                            </Popup>
                        </Marker>
                    ))}

                    {/* Weather Marker */}
                    {layerOrder
                        .filter(layer => layer.type === 'weather')
                        .map((layer, idx) => (
                            <Marker key={layer.id} position={[layer.coords.lat, layer.coords.lon]} icon={weatherIcon}>
                                <Popup>
                                    <strong>Weather Location</strong><br />
                                    {layer.location}<br />
                                    {layer.date}<br />
                                    {layer.data && layer.data.list && layer.data.list.length > 0 && (() => {
                                        const avg = getWeatherAverages(layer.data);
                                        return (
                                            <>
                                                <hr style={{margin: "6px 0"}} />
                                                <b>Weather:</b> {avg.weather}<br />
                                                <b>Avg Temp:</b> {avg.temp} °C<br />
                                                <b>Avg Precip:</b> {avg.precip} mm<br />
                                                <b>Avg Humidity:</b> {avg.humidity} %<br />
                                                <b>Avg Wind:</b> {avg.wind} m/s
                                            </>
                                        );
                                    })()}
                                </Popup>
                            </Marker>
                        ))
                    }

                    {infoPopup && (
                        <Popup
                            position={infoPopup.position}
                            onClose={() => setInfoPopup(null)}
                        >
                            {infoPopup.content}
                        </Popup>
                    )}

                </MapContainer>
                {selectedAlert && (
                <div style={{
                    position: 'absolute',
                    top: 30,
                    right: 30,
                    zIndex: 2000,
                    background: 'white',
                    borderRadius: 8,
                    boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                    padding: 20,
                    minWidth: 250,
                    maxWidth: 350
                }}>
                    <h4 style={{marginTop: 0}}>Fire Alert Details</h4>
                    <p><b>Date:</b> {selectedAlert.acq_date}</p>
                    <p><b>Latitude:</b> {selectedAlert.latitude}</p>
                    <p><b>Longitude:</b> {selectedAlert.longitude}</p>
                    <p><b>Confidence:</b> {selectedAlert.confidence?.toUpperCase()}</p>
                    {selectedAlert.brightness && <p><b>Brightness:</b> {selectedAlert.brightness}</p>}
                    {selectedAlert.satellite && <p><b>Satellite:</b> {selectedAlert.satellite}</p>}
                    <button onClick={() => setSelectedAlert(null)} style={{marginTop: 10}}>Close</button>
                </div>
            )}
                
                {/* Mobile buttons - inside the map container */}
                {isMobile && (
                    <div style={{ 
                        position: 'absolute', 
                        top: '15px', 
                        left: '15px', 
                        zIndex: 1200,
                        display: 'flex',
                        gap: '10px',
                        alignItems: 'center'
                    }}>
                        <IconButton 
                            onClick={toggleSidebarDrawer}
                            sx={{ 
                                width: 44,
                                height: 44,
                                p: 0,
                                borderRadius: '50%',
                                backgroundColor: '#e8e6d9',
                                boxShadow: '0 2px 6px rgba(0,0,0,0.2)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                '&:hover': { backgroundColor: '#d5d3c3' }
                            }}
                            aria-label="Open navigation"
                        >
                            <MenuIcon sx={{ fontSize: 28 }} />
                        </IconButton>
                        
                        <IconButton 
                            onClick={toggleContentDrawer}
                            sx={{ 
                                width: 44,
                                height: 44,
                                p: 0,
                                borderRadius: '50%',
                                backgroundColor: '#f5f5f0',
                                boxShadow: '0 2px 6px rgba(0,0,0,0.2)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                '&:hover': { backgroundColor: '#e5e5e0' }
                            }}
                            aria-label="Open panel"
                        >
                            <span
                                role="img"
                                aria-label={navigationItems.find(item => item.category === activeCategory)?.label}
                                style={{
                                    fontSize: 28, // match MenuIcon size
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    width: '100%',
                                    height: '100%',
                                    textAlign: 'center'
                                }}
                            >
                                {navigationItems.find(item => item.category === activeCategory)?.icon}
                            </span>
                        </IconButton>
                    </div>
                )}
            </div>
        </div>
    );
};

export default Monitoring;